import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CallBtn, WABtn, PhoneField } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, Search, X, IndianRupee, Trash2, Eye, Edit2 } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import ExportButton from '../components/ui/ExportButton';
import { fmtDate, fmtCurrency } from '../lib/exportUtils';
import { courseShortCode } from '../lib/courseUtils';
import * as XLSX from 'xlsx';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Safe helpers (added in review) ──────────────────────────────
// PostgREST returns at most 1000 rows per request — page anything that can be bigger.
async function fetchAllPaged(buildQuery) {
  const first = await buildQuery().range(0, 999);
  if (first.error) throw first.error;
  let all = first.data || [];
  if (all.length < 1000) return all;
  let from = 1000;
  const WAVE = 4;   // pages fetched at the same time (results come back in order)
  while (true) {
    const res = await Promise.all(Array.from({ length: WAVE }, (_, i) => buildQuery().range(from + i * 1000, from + i * 1000 + 999)));
    let done = false;
    for (const { data, error } of res) {
      if (error) throw error;
      all = all.concat(data || []);
      if (!data || data.length < 1000) { done = true; break; }
    }
    if (done) break;
    from += WAVE * 1000;
  }
  return all;
}
// Local (IST) date YYYY-MM-DD. toISOString() is the UTC date = "yesterday" before 5:30am IST.
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Free text made safe for a PostgREST .or() filter (commas / brackets break it; apostrophes must not be doubled).
const cleanSearch = t => String(t || '').replace(/[,()\\*%]/g, ' ').replace(/\s+/g, ' ').trim();
// Account names used by the Accounts module
const ACC_MODES = ['EKM FDO', 'EKM Admin', 'TVM FDO', 'TVM Admin', 'Cash', 'HDFC Vazhakkala', 'HDFC Kottarakkara', 'SBI', 'Ujjivan'];
function accountsMode(creditHead, mode) {
  const ch = String(creditHead || '').replace(/-/g, ' ').trim().toLowerCase();
  const hit = ACC_MODES.find(m => m.toLowerCase() === ch);
  if (hit) return hit;
  if (String(creditHead || '').trim()) return String(creditHead).trim();
  const modeMap = { cash: 'Cash', upi: 'EKM FDO', bank: 'SBI', hdfc: 'HDFC Vazhakkala', ekm_fdo: 'EKM FDO', ekm_admin: 'EKM Admin', tvm_fdo: 'TVM FDO', tvm_admin: 'TVM Admin' };
  return modeMap[String(mode || '').toLowerCase()] || mode || 'Cash';
}
const feeCategory = head => ({ tuition: 'Tuition Fees', hostel: 'Hostel Fees', exam: 'Exam Fees', registration: 'Registration Fees' }[head] || 'Tuition Fees');

// The old Payment Mode field was dropped; the chosen account (same list as Accounts) is the payment mode.
// payment_mode column is still filled (cash / online) so older reports keep working.
// PT-DEFAULT: front-desk / admin accounts default to Cash, every other account to Online
const defaultPayType = acct => { const k = String(acct || '').toUpperCase().replace(/[^A-Z]/g, ''); return ['EKMFDO','EKMADMIN','TVMFDO','TVMADMIN','CASH'].includes(k) ? 'Cash' : 'Online'; };
const legacyPayMode = acct => (String(acct || '').trim().toLowerCase() === 'cash' ? 'cash' : 'online');
const FEE_HEADS = ['tuition', 'hostel', 'attestation', 'package_change', 'manpower', 'other'];

function fmt(n) { return Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }); }

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

// ═══════════════ Fee receipts, audit log and reports (added) ═══════════════
const FEE_AUDIT_SQL = `create table if not exists fee_audit_log (
  id uuid primary key default gen_random_uuid(),
  created_at timestamptz default now(),
  action text,
  student_id text,
  student_name text,
  register_number text,
  receipt_number text,
  amount numeric,
  detail text,
  by_email text
);
alter table fee_audit_log enable row level security;
create policy "fee_audit_all" on fee_audit_log for all to authenticated using (true) with check (true);`;

// Best-effort: never blocks the real action
async function logFeeAudit(byEmail, action, st, extra = {}) {
  try {
    await supabase.from('fee_audit_log').insert({
      action, student_id: st?.id != null ? String(st.id) : null, student_name: st?.full_name || null,
      register_number: st?.register_number || null, receipt_number: extra.receipt_number || null,
      amount: extra.amount != null ? Number(extra.amount) : null, detail: extra.detail || null, by_email: byEmail || null,
    });
  } catch (e) { /* table may not exist yet */ }
}

// Sequential receipt numbers per financial year: RCP/26-27/00001
function financialYearTag(dateStr) {
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(2)}-${String(y + 1).slice(2)}`;
}
async function nextReceiptNo(dateStr, skip = 0) {
  const prefix = `RCP/${financialYearTag(dateStr)}/`;
  try {
    const { data } = await supabase.from('fee_payments').select('receipt_number')
      .like('receipt_number', prefix + '%').order('receipt_number', { ascending: false }).limit(1);
    const last = data && data[0] ? parseInt(String(data[0].receipt_number).slice(prefix.length), 10) : 0;
    return prefix + String((isNaN(last) ? 0 : last) + 1 + skip).padStart(5, '0');
  } catch (e) { return `RCP-${Date.now()}`; }
}

function rupeesInWords(n) {
  n = Math.round(Math.abs(Number(n) || 0));
  if (!n) return 'Zero Rupees Only';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = x => x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ' ' + a[x % 10] : '');
  const three = x => (x >= 100 ? a[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' : '') : '') + (x % 100 ? two(x % 100) : '');
  const parts = [];
  const cr = Math.floor(n / 10000000); n %= 10000000;
  const lk = Math.floor(n / 100000); n %= 100000;
  const th = Math.floor(n / 1000); n %= 1000;
  if (cr) parts.push(three(cr) + ' Crore');
  if (lk) parts.push(two(lk) + ' Lakh');
  if (th) parts.push(two(th) + ' Thousand');
  if (n) parts.push(three(n));
  return parts.join(' ') + ' Rupees Only';
}

const escHtml = t => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function printReceipt(p, st, sum) {
  const w = window.open('', '_blank', 'width=820,height=900');
  if (!w) { toast.error('Allow pop-ups to print the receipt'); return; }
  const money = n => '₹' + Number(n || 0).toLocaleString('en-IN');
  const row = (l, v) => `<tr><td class="l">${l}</td><td>${escHtml(v)}</td></tr>`;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${escHtml(p.receipt_number)}</title>
<style>
 body{font-family:Arial,sans-serif;color:#111;margin:0;padding:24px}
 .box{border:2px solid #0f766e;border-radius:8px;max-width:720px;margin:0 auto;padding:22px 26px}
 h1{margin:0;font-size:22px;color:#0f766e;text-align:center}
 .sub{text-align:center;font-size:12px;color:#555;margin:2px 0 14px}
 .title{text-align:center;font-weight:700;letter-spacing:2px;border-top:1px solid #ccc;border-bottom:1px solid #ccc;padding:6px 0;margin-bottom:14px}
 table{width:100%;border-collapse:collapse;font-size:14px}
 td{padding:7px 4px;border-bottom:1px dotted #bbb}
 td.l{width:38%;color:#555}
 .amt{font-size:22px;font-weight:800;color:#0f766e;margin:14px 0 2px}
 .words{font-size:12px;color:#444;margin-bottom:14px}
 .sum{background:#f1f5f4;border-radius:6px;padding:8px 12px;margin-top:10px;font-size:13px;display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap}
 .sig{display:flex;justify-content:space-between;margin-top:46px;font-size:12px;color:#444}
 .sig div{border-top:1px solid #444;padding-top:4px;min-width:150px;text-align:center}
 .note{font-size:11px;color:#777;margin-top:16px;text-align:center}
 @media print{.np{display:none} body{padding:0}}
</style></head><body>
<div class="box">
 <h1>Armstech Engineers Pvt Ltd</h1>
 <div class="sub">Training Institute · Ernakulam &amp; Thiruvananthapuram · mail@armstechtraining.in</div>
 <div class="title">FEE RECEIPT</div>
 <table>
  ${row('Receipt No.', p.receipt_number)}
  ${row('Date', fmtDate ? fmtDate(p.payment_date) : p.payment_date)}
  ${row('Received from', st?.full_name)}
  ${row('Register No.', st?.register_number)}
  ${row('Batch', st?.batch_name || '')}
  ${row('Fee Head', String(p.fee_head || '').replace('_', ' '))}
  ${p.payment_type ? row('Payment Type', p.payment_type) : ''}
  ${row('Payment Mode', p.credit_head || String(p.payment_mode || '').toUpperCase())}
  ${p.transaction_ref ? row('Reference', p.transaction_ref) : ''}
  ${p.remarks ? row('Remarks', p.remarks) : ''}
 </table>
 <div class="amt">${money(p.amount)}</div>
 <div class="words">${escHtml(rupeesInWords(p.amount))}</div>
 ${sum ? `<div class="sum"><span>Total fees: <b>${money(sum.totalFees)}</b></span><span>Paid till date: <b>${money(sum.totalPaid)}</b></span><span>Balance: <b>${money(sum.balance)}</b></span></div>` : ''}
 <div class="sig"><div>Student / Guardian</div><div>Authorised Signatory</div></div>
 <div class="note">Fees once paid are non-refundable. This is a computer generated receipt.</div>
</div>
<div class="np" style="text-align:center;margin-top:16px"><button onclick="window.print()" style="padding:8px 22px;font-size:14px">Print</button></div>
<script>setTimeout(function(){window.print()},400)</script>
</body></html>`);
  w.document.close();
}

const waNumber = raw => {
  const ph = String(raw || '').split('/').pop().replace(/\D/g, '');
  return ph.startsWith('91') ? ph : ph.length === 10 ? '91' + ph : ph;
};

function FeeReports({ batches, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('overdue');
  const [busy, setBusy] = useState(false);
  const [rowsState, setRowsState] = useState({ tab: '', day: '', list: [] });
  const runSeq = React.useRef(0);
  const [auditMissing, setAuditMissing] = useState(false);
  const [batchF, setBatchF] = useState('');
  const [minDays, setMinDays] = useState(0);
  const [q, setQ] = useState('');
  const [day, setDay] = useState(localDate());
  const STU_COLS = 'id, full_name, register_number, batch_id, batch_name, contact_number, whatsapp_number, guardian_contact, guardian_name, student_status, is_active, fee_total';

  async function studentsByIds(ids) {
    let out = [];
    for (let i = 0; i < ids.length; i += 100) {
      const { data, error } = await supabase.from('students').select(STU_COLS).in('id', ids.slice(i, i + 100));
      if (error) throw error;
      out = out.concat(data || []);
    }
    return out;
  }

  async function run() {
    const mySeq = ++runSeq.current, myTab = tab, myDay = day;
    const setRows = list => { if (mySeq === runSeq.current) setRowsState({ tab: myTab, day: myDay, list }); };
    setBusy(true); setAuditMissing(false);
    try {
      if (tab === 'overdue') {
        const t = localDate();
        const sch = await fetchAllPaged(() => supabase.from('student_fee_schedules')
          .select('student_id, installment_number, amount, paid_amount, status, due_date')
          .in('status', ['pending', 'partial']).lt('due_date', t)
          .order('due_date').order('student_id').order('installment_number'));
        const stus = await studentsByIds([...new Set(sch.map(x => x.student_id))]);
        const sm = {}; stus.forEach(s => { sm[s.id] = s; });
        const g = {};
        sch.forEach(x => {
          const s = sm[x.student_id];
          if (!s || s.is_active === false || ['Discontinued', 'Shifted'].includes(s.student_status)) return;
          const pend = Number(x.amount || 0) - Number(x.paid_amount || 0);
          if (pend <= 0) return;
          const days = Math.floor((new Date(t) - new Date(x.due_date)) / 86400000);
          const r = g[x.student_id] || (g[x.student_id] = { s, pending: 0, count: 0, oldest: x.due_date, days: 0 });
          r.pending += pend; r.count++; r.days = Math.max(r.days, days);
          if (x.due_date < r.oldest) r.oldest = x.due_date;
        });
        setRows(Object.values(g).sort((a, b) => b.days - a.days));
      } else if (tab === 'dues') {
        const stus = await fetchAllPaged(() => supabase.from('students').select(STU_COLS)
          .eq('is_active', true).in('student_status', ['Discontinued', 'Shifted']).order('id'));
        const paid = {};
        for (let i = 0; i < stus.length; i += 100) {
          const ids = stus.slice(i, i + 100).map(s => s.id);
          const pays = await fetchAllPaged(() => supabase.from('fee_payments').select('student_id, amount').in('student_id', ids).order('id'));
          pays.forEach(p => { paid[p.student_id] = (paid[p.student_id] || 0) + Number(p.amount || 0); });
        }
        setRows(stus.map(s => ({ s, total: Number(s.fee_total || 0), paid: paid[s.id] || 0, pending: Number(s.fee_total || 0) - (paid[s.id] || 0) }))
          .filter(r => r.pending > 0).sort((a, b) => b.pending - a.pending));
      } else if (tab === 'dayend') {
        const pays = await fetchAllPaged(() => supabase.from('fee_payments')
          .select('id, student_id, amount, payment_date, fee_head, payment_mode, payment_type, credit_head, receipt_number, transaction_ref, students(full_name, register_number, batch_name)')
          .eq('payment_date', day).order('id'));
        setRows(pays);
      } else if (tab === 'audit') {
        const { data, error } = await supabase.from('fee_audit_log').select('*').order('created_at', { ascending: false }).limit(300);
        if (error) setAuditMissing(true); else setRows(data || []);
      }
    } catch (e) { toast.error('Report failed: ' + e.message); }
    if (mySeq === runSeq.current) setBusy(false);
  }
  // Only ever show rows that belong to the tab (and day) on screen — a tab switch must not render the previous tab's rows
  const rows = (rowsState.tab === tab && (tab !== 'dayend' || rowsState.day === day)) ? rowsState.list : [];
  useEffect(() => { if (open) run(); }, [open, tab, day]); // eslint-disable-line

  const money = n => '₹' + Number(n || 0).toLocaleString('en-IN');
  function remind(r, who) {
    const s = r.s;
    const nm = who === 'g' ? (s.guardian_name || 'Guardian') : s.full_name;
    const ph = waNumber(who === 'g' ? s.guardian_contact : (s.whatsapp_number || s.contact_number));
    if (!ph) { toast.error('No phone number'); return; }
    const body = tab === 'overdue'
      ? `${r.count} installment${r.count > 1 ? 's are' : ' is'} overdue (oldest due ${r.oldest}). Pending amount: ${money(r.pending)}.`
      : `A balance of ${money(r.pending)} is pending against the course fees.`;
    const msg = `Dear ${who === 'g' ? 'Mr/Ms ' : ''}${nm},\n\nThis is a gentle reminder from *Armstech Engineers Pvt Ltd*${who === 'g' ? ' regarding your ward ' + s.full_name + ' (' + s.register_number + ')' : ''}.\n${body}\n\nKindly clear the dues at the earliest. Please ignore this message if already paid.\n\nThank you.`;
    window.open(`whatsapp://send?phone=${ph}&text=${encodeURIComponent(msg)}`, '_self');
  }
  function exportXlsx(name, data) {
    if (!data.length) { toast.error('Nothing to export'); return; }
    saveStyledXlsx(`${name}_${localDate()}.xlsx`, [{ name: 'Report', title: String(name).replace(/_/g, ' '), rows: data }]);
  }

  const filtered = (tab === 'overdue' || tab === 'dues') ? rows.filter(r => {
    if (batchF && r.s.batch_name !== batchF) return false;
    if (tab === 'overdue' && r.days < Number(minDays || 0)) return false;
    if (q) { const t = q.toLowerCase(); if (!(`${r.s.full_name} ${r.s.register_number}`.toLowerCase().includes(t))) return false; }
    return true;
  }) : rows;
  const sumPending = filtered.reduce((a, r) => a + (r.pending || 0), 0);
  const batchNames = [...new Set((batches || []).map(b => b.batch_name))];   // already ongoing-first, newest-first

  const th = { padding: '6px 10px', textAlign: 'left', background: '#e6f7f5', position: 'sticky', top: 0 };
  const td = { padding: '5px 10px', borderBottom: '1px solid var(--border)' };
  const tabBtn = (k, l) => <button key={k} className={tab === k ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setTab(k)}>{l}</button>;

  // day-end summary
  const byHead = {}, byFee = {};
  if (tab === 'dayend') rows.forEach(p => {
    const k = accountsMode(p.credit_head, p.payment_mode);
    byHead[k] = (byHead[k] || 0) + Number(p.amount || 0);
    byFee[p.fee_head] = (byFee[p.fee_head] || 0) + Number(p.amount || 0);
  });
  const dayTotal = rows.reduce((a, p) => a + Number(p.amount || 0), 0);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 24 }}>
      <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>📑 Fee Reports — Overdue · Discontinued Dues · Day-end · Audit Log</div>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
            {tabBtn('overdue', '⏰ Overdue Installments')}
            {tabBtn('dues', '🚪 Discontinued / Shifted Dues')}
            {tabBtn('dayend', '🧾 Day-end Collection')}
            {isAdmin && tabBtn('audit', '🕵 Audit Log')}
          </div>

          {(tab === 'overdue' || tab === 'dues') && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              <select value={batchF} onChange={e => setBatchF(e.target.value)} style={{ fontSize: 13, padding: '4px 8px' }}>
                <option value="">All batches</option>
                {batchNames.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              {tab === 'overdue' && <label style={{ fontSize: 12 }}>Overdue ≥ <input type="number" min="0" value={minDays} onChange={e => setMinDays(e.target.value)} style={{ width: 60, fontSize: 13, padding: '3px 6px' }} /> days</label>}
              <input placeholder="Search name / reg no" value={q} onChange={e => setQ(e.target.value)} style={{ fontSize: 13, padding: '4px 8px' }} />
              <span style={{ fontSize: 13 }}>{filtered.length} students · Pending <strong style={{ color: 'var(--red)' }}>{money(sumPending)}</strong></span>
              <button className="btn btn-secondary btn-sm" onClick={() => exportXlsx(tab === 'overdue' ? 'Overdue_Installments' : 'Discontinued_Dues', filtered.map((r, i) => ({
                'S.No': i + 1, 'Name': r.s.full_name, 'Register No': r.s.register_number, 'Batch': r.s.batch_name || '',
                'Status': r.s.student_status || '', 'Contact': r.s.contact_number || '', 'Guardian Contact': r.s.guardian_contact || '',
                ...(tab === 'overdue' ? { 'Overdue Installments': r.count, 'Oldest Due': r.oldest, 'Days Overdue': r.days } : { 'Total Fees': r.total, 'Paid': r.paid }),
                'Pending (₹)': r.pending,
              })))}>⬇ Export</button>
            </div>
          )}

          {busy && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>}

          {!busy && tab === 'overdue' && (
            <div style={{ overflow: 'auto', maxHeight: 420 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr><th style={th}>Student</th><th style={th}>Batch</th><th style={th}>Installments</th><th style={th}>Oldest due</th><th style={th}>Days</th><th style={{ ...th, textAlign: 'right' }}>Pending</th><th style={th}>Remind</th></tr></thead>
                <tbody>{filtered.map(r => (
                  <tr key={r.s.id}>
                    <td style={td}><strong>{r.s.full_name}</strong><div style={{ fontSize: 10, color: 'var(--text3)' }}>{r.s.register_number}</div></td>
                    <td style={td}>{r.s.batch_name}</td><td style={td}>{r.count}</td><td style={td}>{r.oldest}</td>
                    <td style={{ ...td, color: r.days > 30 ? 'var(--red)' : 'var(--amber)', fontWeight: 700 }}>{r.days}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(r.pending)}</td>
                    <td style={td}><button className="btn btn-ghost btn-sm" style={{ color: '#25d366' }} onClick={() => remind(r, 's')}>💬 Student</button>
                      {r.s.guardian_contact && <button className="btn btn-ghost btn-sm" style={{ color: '#128c7e' }} onClick={() => remind(r, 'g')}>💬 Guardian</button>}</td>
                  </tr>))}
                  {!filtered.length && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: 'var(--text3)' }}>No overdue installments 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {!busy && tab === 'dues' && (
            <div style={{ overflow: 'auto', maxHeight: 420 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr><th style={th}>Student</th><th style={th}>Batch</th><th style={th}>Status</th><th style={{ ...th, textAlign: 'right' }}>Total</th><th style={{ ...th, textAlign: 'right' }}>Paid</th><th style={{ ...th, textAlign: 'right' }}>Pending</th><th style={th}>Remind</th></tr></thead>
                <tbody>{filtered.map(r => (
                  <tr key={r.s.id}>
                    <td style={td}><strong>{r.s.full_name}</strong><div style={{ fontSize: 10, color: 'var(--text3)' }}>{r.s.register_number}</div></td>
                    <td style={td}>{r.s.batch_name}</td><td style={td}>{r.s.student_status}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{money(r.total)}</td><td style={{ ...td, textAlign: 'right' }}>{money(r.paid)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--red)' }}>{money(r.pending)}</td>
                    <td style={td}><button className="btn btn-ghost btn-sm" style={{ color: '#25d366' }} onClick={() => remind(r, 's')}>💬 Student</button>
                      {r.s.guardian_contact && <button className="btn btn-ghost btn-sm" style={{ color: '#128c7e' }} onClick={() => remind(r, 'g')}>💬 Guardian</button>}</td>
                  </tr>))}
                  {!filtered.length && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: 'var(--text3)' }}>No pending dues</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {!busy && tab === 'dayend' && (
            <div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <DateInput value={day} max={localDate()} onChange={e => setDay(e.target.value)} style={{ fontSize: 13, padding: '4px 8px' }} />
                <span style={{ fontSize: 13 }}>{rows.length} transactions · Net total <strong style={{ color: 'var(--green)' }}>{money(dayTotal)}</strong></span>
                <button className="btn btn-secondary btn-sm" onClick={() => exportXlsx('Day_end_' + day, rows.map((p, i) => ({
                  'S.No': i + 1, 'Receipt': p.receipt_number || '', 'Student': p.students?.full_name || '', 'Register No': p.students?.register_number || '',
                  'Fee Head': p.fee_head, 'Payment Type': p.payment_type || '', 'Mode': p.payment_mode, 'Account': accountsMode(p.credit_head, p.payment_mode), 'Amount (₹)': Number(p.amount || 0), 'Ref': p.transaction_ref || '',
                })))}>⬇ Export</button>
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10, fontSize: 12 }}>
                <div><strong>By account</strong>{Object.entries(byHead).map(([k, v]) => <div key={k}>{k}: <strong>{money(v)}</strong></div>)}</div>
                <div><strong>By fee head</strong>{Object.entries(byFee).map(([k, v]) => <div key={k}>{k}: <strong>{money(v)}</strong></div>)}</div>
              </div>
              <div style={{ overflow: 'auto', maxHeight: 320 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr><th style={th}>Receipt</th><th style={th}>Student</th><th style={th}>Fee head</th><th style={th}>Payment Type</th><th style={th}>Account</th><th style={{ ...th, textAlign: 'right' }}>Amount</th></tr></thead>
                  <tbody>{rows.map(p => (
                    <tr key={p.id}><td style={td}>{p.receipt_number}</td><td style={td}>{p.students?.full_name}<div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.students?.register_number}</div></td>
                      <td style={td}>{p.fee_head}</td><td style={td}>{p.payment_type || '—'}</td><td style={td}>{accountsMode(p.credit_head, p.payment_mode)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: Number(p.amount) < 0 ? 'var(--red)' : undefined }}>{money(p.amount)}</td></tr>))}
                    {!rows.length && <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: 'var(--text3)' }}>No collections on this date</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!busy && tab === 'audit' && (auditMissing ? (
            <div>
              <div style={{ fontSize: 13, marginBottom: 6 }}>The audit table is not created yet. Run this once in the Supabase SQL Editor:</div>
              <pre style={{ background: '#111', color: '#9fe', padding: 10, borderRadius: 6, fontSize: 11, overflow: 'auto' }}>{FEE_AUDIT_SQL}</pre>
              <button className="btn btn-secondary btn-sm" onClick={() => navigator.clipboard.writeText(FEE_AUDIT_SQL).then(() => toast.success('SQL copied'))}>Copy SQL</button>
            </div>
          ) : (
            <div style={{ overflow: 'auto', maxHeight: 420 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr><th style={th}>When</th><th style={th}>Action</th><th style={th}>Student</th><th style={th}>Receipt</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Detail</th><th style={th}>By</th></tr></thead>
                <tbody>{rows.map(a => (
                  <tr key={a.id}><td style={td}>{new Date(a.created_at).toLocaleString('en-IN')}</td><td style={td}><strong>{a.action}</strong></td>
                    <td style={td}>{a.student_name}<div style={{ fontSize: 10, color: 'var(--text3)' }}>{a.register_number}</div></td>
                    <td style={td}>{a.receipt_number}</td><td style={{ ...td, textAlign: 'right' }}>{a.amount != null ? money(a.amount) : ''}</td>
                    <td style={td}>{a.detail}</td><td style={td}>{a.by_email}</td></tr>))}
                  {!rows.length && <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: 'var(--text3)' }}>No entries yet</td></tr>}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StudentRow = React.memo(function StudentRow({ s, active, onPick }) {
  return (
            <div onClick={() => onPick(s)} style={{ padding: '10px 16px', cursor: 'pointer', background: active ? 'var(--accent-light)' : 'transparent', borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent', borderBottom: '1px solid var(--border)', transition: 'all 0.15s' }}>
              <div style={{ fontWeight: 500, fontSize: 13, color: active ? 'var(--accent)' : 'var(--text)' }}>{s.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace', marginTop: 2 }}>{s.register_number}</div>
              {s.course_name && <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 1, fontWeight: 600 }}>{courseShortCode(s.course_name)}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                {s.batches?.batch_name && (
                  <span style={{ fontSize: 10, background: 'var(--accent-light)', color: 'var(--accent)', padding: '1px 6px', borderRadius: 4 }}>
                    {s.batches.batch_name}
                  </span>
                )}
                {s.contact_number && (
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                    📞 <PhoneField phone=<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace',fontSize:12}}>{s.contact_number}</span>{s.contact_number&&<CallBtn phone={s.contact_number}/>}</div>/>
                  </span>
                )}
              </div>
            </div>
  );
});

export default function Fees() {
  const { isEditor, isAdmin, staff, canEditBatch, canEdit, user } = useAuth();
  const canApproveAdj = !!(isEditor || isAdmin);
  const searchSeq = React.useRef(0);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [discountLogs, setDiscountLogs] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountForm, setDiscountForm] = useState({amount:'',reason:'',approved_by:''});
  const [adjustments, setAdjustments] = useState([]);
  const [showAdjModal, setShowAdjModal] = useState(false);
  const [adjForm, setAdjForm] = useState({type:'refund',amount:'',reason:'',payment_mode:'Cash',reference_no:'',notes:'',to_student_id:''});
  const sfa = (k,v) => setAdjForm(f=>({...f,[k]:v}));
  const [showAdjust, setShowAdjust] = useState(false);
  const [showFeeImport, setShowFeeImport] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importBatch, setImportBatch] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importDone, setImportDone] = useState(null);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [batches, setBatches]   = useState([]);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null);
  const canEditSelected = canEdit('fees') || (selected?.batch_id && canEditBatch ? canEditBatch(selected?.batch_id) : false);
  const [studentPayments, setStudentPayments] = useState([]);
  const [utilityBills, setUtilityBills] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const selectStudentRef = React.useRef(null);
  const pickStudent = useCallback(st => { if (selectStudentRef.current) selectStudentRef.current(st); }, []);
  useEffect(() => {
    const t = setTimeout(async () => {
      setSearchQ(search);
      const sv = cleanSearch(search);
      const seq = ++searchSeq.current;
      if (!filterBatch && sv.length >= 2) {
        const { data } = await supabase.from('students')
          .select('id, full_name, register_number, contact_number, fee_total, fee_tuition, hostel_fee, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_needed, batch_id, batch_name, course_name, batches(batch_name, batch_code)')
          .eq('is_active', true)
          .or(`full_name.ilike.%${sv}%,register_number.ilike.%${sv}%,contact_number.ilike.%${sv}%`)
          .order('full_name').limit(100);
        if (seq === searchSeq.current) setStudents(data || []);
      } else if (!filterBatch && search.length === 0) {
        setStudents([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, filterBatch]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [editPayment, setEditPayment]   = useState(null);
  const [studentSchedule, setStudentSchedule] = useState([]); // fee installment schedule
  const [schedulePreview, setSchedulePreview] = useState(null); // payment distribution preview
  const [showSchedule, setShowSchedule] = useState(false);
  const [viewPayment, setViewPayment]   = useState(null);
  const [acctModes, setAcctModes] = useState(ACC_MODES);
  useEffect(() => {
    supabase.from('accounts_payment_modes').select('name').order('name').then(({ data }) => {
      const n = (data || []).map(x => x.name).filter(Boolean);
      if (n.length) setAcctModes(n);
    });
  }, []);
  const [form, setForm] = useState({ fee_head: 'tuition', amount: '', payment_mode: 'cash', payment_type: 'Cash', payment_date: localDate(), credit_head: '', transaction_ref: '', remarks: '' });


  async function loadStudents() {
    const { data: b } = await supabase.from('batches').select('id, batch_name, status, start_date').order('batch_name');
    setBatches(sortBatches(b || []));
    // Load all students for batch summary
    try {
      const allS = await fetchAllPaged(() => supabase.from('students')
        .select('id, full_name, register_number, batch_id, batch_name, fee_total')
        .eq('is_active', true)
        .not('student_status','in','("Discontinued","Shifted")')
        .order('id'));
      setAllStudents(allS || []);
    } catch (e) { toast.error('Could not load students: ' + e.message); }
    setLoading(false);
  }

  async function loadStudentsByBatch(batchName) {
    if (!batchName) { setStudents([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('students')
      .select('id, full_name, register_number, contact_number, fee_total, fee_tuition, hostel_fee, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_needed, batch_id, batch_name, course_name, student_status, batches(batch_name, batch_code)')
      .eq('is_active', true)
      .eq('batch_name', batchName)
      .neq('student_status','Discontinued')
      .not('student_status','in','("Discontinued","Shifted")')
      .order('register_number')
      .limit(6000);
    setStudents(data || []);
    setLoading(false);
  }

  async function loadAdjustments(sid) {
    const {data} = await supabase.from('fee_adjustments')
      .select('*').or(`from_student_id.eq.${sid},to_student_id.eq.${sid}`)
      .order('created_at',{ascending:false});
    setAdjustments(data||[]);
  }

  async function saveDiscount() {
    if (!discountForm.amount||Number(discountForm.amount)<=0) { toast.error('Enter valid amount'); return; }
    if (!discountForm.reason) { toast.error('Enter reason'); return; }
    // Update student fee_discount AND fee_total (fee_total = all fee heads − discount, same formula as the Students page)
    const newDiscount = Number(selected?.fee_discount||0) + Number(discountForm.amount);
    const grossFees = ['fee_tuition','hostel_fee','fee_attestation','fee_package_change','fee_manpower','fee_certification','fee_other']
      .reduce((a,k)=>a+Number(selected?.[k]||0),0);
    if (newDiscount > grossFees) { toast.error(`Total discount (₹${newDiscount.toLocaleString('en-IN')}) cannot exceed total fees (₹${grossFees.toLocaleString('en-IN')})`); return; }
    const {error} = await supabase.from('students').update({fee_discount: newDiscount, fee_total: grossFees - newDiscount}).eq('id', selected.id);
    if (error) { toast.error(error.message); return; }
    // Log it
    const {error: logErr} = await supabase.from('fee_discount_log').insert({
      student_id: selected.id,
      amount: Number(discountForm.amount),
      reason: discountForm.reason,
      approved_by: discountForm.approved_by,
      created_by: user?.email||'',
    });
    if (logErr) toast.error('Discount applied but the log entry failed: ' + logErr.message);
    logFeeAudit(user?.email, 'discount', selected, { amount: discountForm.amount, detail: `${discountForm.reason || ''} (approved by ${discountForm.approved_by || '—'}); total discount now ₹${newDiscount}` });
    toast.success('Discount applied and logged');
    setShowDiscountModal(false);
    setDiscountForm({amount:'',reason:'',approved_by:''});
    // Reload discount logs
    const {data} = await supabase.from('fee_discount_log').select('*').eq('student_id',selected.id).order('created_at',{ascending:false});
    setDiscountLogs(data||[]);
    // Reload student
    selectStudent(selected);
  }

  async function saveAdjustment() {
    if (!adjForm.amount || Number(adjForm.amount)<=0) { toast.error('Enter valid amount'); return; }
    if (adjForm.type==='transfer' && !adjForm.to_student_id) { toast.error('Select recipient student'); return; }
    if (Number(adjForm.amount) > totalPaid) { toast.error(`Amount cannot exceed the total paid (₹${totalPaid.toLocaleString('en-IN')})`); return; }
    if (!adjForm.reason.trim()) { toast.error('Enter a reason'); return; }
    const payload = {
      ...adjForm,
      amount: Number(adjForm.amount),
      from_student_id: selected?.id,
      to_student_id: adjForm.type==='transfer' ? adjForm.to_student_id : null,
      created_by: user?.email||'',
      status: 'pending',
    };
    const {error} = await supabase.from('fee_adjustments').insert(payload);
    if (error) { toast.error(error.message); return; }
    toast.success('Request submitted for approval');
    setShowAdjModal(false);
    setAdjForm({type:'refund',amount:'',reason:'',payment_mode:'Cash',reference_no:'',notes:'',to_student_id:''});
    loadAdjustments(selected?.id);
  }

  async function approveAdjustment(adj) {
    if (!canApproveAdj) { toast.error('Only an editor or admin can approve refunds / transfers'); return; }
    if (adj.created_by && adj.created_by === user?.email && !isAdmin) { toast.error('You cannot approve your own request'); return; }
    if (!window.confirm('Approve this request? This will update fee records.')) return;
    const nowIso = new Date().toISOString();
    // Claim the request first so a double-click / second approver cannot apply it twice
    const { data: claimed, error: cErr } = await supabase.from('fee_adjustments')
      .update({ status:'approved', approved_by: user?.email||'', approved_at: nowIso })
      .eq('id', adj.id).eq('status','pending').select('id');
    if (cErr) { toast.error(cErr.message); return; }
    if (!claimed || !claimed.length) { toast.error('This request was already processed'); loadAdjustments(selected?.id); return; }
    const undoClaim = () => supabase.from('fee_adjustments').update({ status:'pending', approved_by:null, approved_at:null }).eq('id', adj.id);
    try {
      // The student must really have paid at least this much
      const { data: pays } = await supabase.from('fee_payments').select('amount').eq('student_id', adj.from_student_id);
      const paid = (pays||[]).reduce((a,x)=>a+Number(x.amount||0),0);
      if (Number(adj.amount) > paid) { await undoClaim(); toast.error(`Student has only paid ₹${paid.toLocaleString('en-IN')} — cannot apply ₹${Number(adj.amount).toLocaleString('en-IN')}`); return; }
      const day = localDate();
      const stamp = Date.now();
      let rows;
      if (adj.type==='refund') {
        rows = [{ student_id: adj.from_student_id, receipt_number: `REF-${stamp}`, fee_head: 'tuition', amount: -Number(adj.amount), payment_date: day,
          payment_mode: String(adj.payment_mode||'cash').toLowerCase(), transaction_ref: adj.reference_no||'', remarks: `Refund: ${adj.reason||''}`, received_by: staff?.id }];
      } else {
        rows = [
          { student_id: adj.from_student_id, receipt_number: `TRF-${stamp}-OUT`, fee_head: 'tuition', amount: -Number(adj.amount), payment_date: day,
            payment_mode: 'transfer', transaction_ref: '', remarks: `Transferred to another student: ${adj.reason||''}`, received_by: staff?.id },
          { student_id: adj.to_student_id, receipt_number: `TRF-${stamp}-IN`, fee_head: 'tuition', amount: Number(adj.amount), payment_date: day,
            payment_mode: 'transfer', transaction_ref: '', remarks: `Transferred from another student: ${adj.reason||''}`, received_by: staff?.id },
        ];
      }
      const { error: pErr } = await supabase.from('fee_payments').insert(rows);
      if (pErr) { await undoClaim(); toast.error('Could not apply: ' + pErr.message); return; }
      if (adj.type==='refund') {
        const stu = selected && selected.id===adj.from_student_id ? selected : null;
        const { error: aErr } = await supabase.from('accounts_transactions').insert({
          company: 'ARMSTECH', date: day, type: 'Cash Out', category: 'Fee Refund',
          payment_mode: accountsMode('', adj.payment_mode), amount: Number(adj.amount), reference: rows[0].receipt_number,
          description: `Fee refund — ${stu?.full_name||'student'}${stu?.register_number?' ('+stu.register_number+')':''}: ${adj.reason||''}`, source: 'fees', entry_by: staff?.full_name || null,
        });
        if (aErr) toast.error('Refund recorded, but it could not be posted to Accounts: ' + aErr.message);
      }
      await redistributeAllPayments(adj.from_student_id);
      if (adj.type==='transfer') await redistributeAllPayments(adj.to_student_id);
      toast.success('Approved and applied');
      logFeeAudit(user?.email, 'approve_' + adj.type, selected, { amount: adj.amount, detail: adj.reason || '' });
    } catch (e) { await undoClaim(); toast.error('Failed: ' + e.message); }
    loadAdjustments(selected?.id);
    if (selected) selectStudent(selected);
    loadAllPayments();
  }

  async function rejectAdjustment(adj) {
    if (!canApproveAdj) { toast.error('Only an editor or admin can reject requests'); return; }
    const reason = window.prompt('Rejection reason:');
    if (reason===null) return;
    await supabase.from('fee_adjustments').update({
      status:'rejected', approved_by: user?.email||'', approved_at: new Date().toISOString(), notes: reason
    }).eq('id', adj.id).eq('status','pending');
    toast.success('Rejected');
    loadAdjustments(selected?.id);
  }

  async function selectStudent(s) {
    // Reload fresh student data to get latest fee_total
    const { data: fresh } = await supabase.from('students')
      .select('id, full_name, register_number, contact_number, whatsapp_number, guardian_name, guardian_contact, fee_total, fee_tuition, hostel_fee, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_needed, batch_id, batch_name')
      .eq('id', s.id).single();
    setSelected(fresh || s);
    // Load fee schedule
    const {data: schedData} = await supabase.from('student_fee_schedules')
      .select('*').eq('student_id', s.id).order('installment_number');
    setStudentSchedule(schedData||[]);
    const [paymentsRes, utilityRes] = await Promise.all([
      supabase.from('fee_payments').select('*').eq('student_id', s.id).order('payment_date', { ascending: false }),
      supabase.from('hostel_utility_bills').select('*').eq('student_id', s.id).order('billing_year', { ascending: false }).order('billing_month', { ascending: false }),
    ]);
    setStudentPayments(paymentsRes.data || []);
    setUtilityBills(utilityRes.data || []);
    loadAdjustments(s.id);
    supabase.from('fee_discount_log').select('*').eq('student_id',s.id).order('created_at',{ascending:false}).then(({data})=>setDiscountLogs(data||[]));
  }

  async function redistributeAllPayments(studentId) {
    // Reset all schedules
    await supabase.from('student_fee_schedules')
      .update({paid_amount:0, status:'pending', paid_date:null, payment_dates:[], payment_amounts:[]})
      .eq('student_id', studentId);
    // Get all payments in date order
    const {data: allPayments} = await supabase.from('fee_payments')
      .select('amount,payment_date').eq('student_id', studentId).order('payment_date');
    if (!allPayments?.length) return;
    // Get schedules
    const {data: scheds} = await supabase.from('student_fee_schedules')
      .select('*').eq('student_id', studentId).order('installment_number');
    if (!scheds?.length) return;
    const schedState = scheds.map(sc=>({...sc, runningPaid:0, paymentDates:[], paymentAmounts:[]}));
    // Refund / transfer-out rows are negative: take them off the most recent payments first
    let refundLeft = allPayments.filter(x=>Number(x.amount)<0).reduce((a,x)=>a+Math.abs(Number(x.amount)),0);
    const netPayments = allPayments.filter(x=>Number(x.amount)>0).map(x=>({ ...x, amt: Number(x.amount) }));
    for (let i = netPayments.length-1; i>=0 && refundLeft>0; i--) {
      const cut = Math.min(netPayments[i].amt, refundLeft);
      netPayments[i].amt -= cut; refundLeft -= cut;
    }
    // Apply each payment across schedules in order
    for (const payment of netPayments) {
      let remaining = payment.amt;
      for (const sc of schedState) {
        if (remaining <= 0) break;
        const due = Number(sc.amount) - sc.runningPaid;
        if (due <= 0) continue;
        const applying = Math.min(remaining, due);
        sc.runningPaid += applying;
        sc.paymentDates.push(payment.payment_date);
        sc.paymentAmounts.push(applying);
        remaining -= applying;
      }
    }
    // Save each schedule
    for (const sc of schedState) {
      if (sc.runningPaid === 0) continue;
      const lastDate = sc.paymentDates[sc.paymentDates.length-1];
      await supabase.from('student_fee_schedules').update({
        paid_amount: sc.runningPaid,
        status: sc.runningPaid >= Number(sc.amount) ? 'paid' : 'partial',
        paid_date: sc.runningPaid >= Number(sc.amount) ? lastDate : null,
        payment_dates: sc.paymentDates,
        payment_amounts: sc.paymentAmounts,
      }).eq('id', sc.id);
    }
  }

  async function deletePayment(p) {
    if (!window.confirm(`Delete payment ${p.receipt_number||''} of ₹${Number(p.amount).toLocaleString('en-IN')}?\n\nThe matching Accounts entry (if any) will also be removed. This cannot be undone.`)) return;
    const { error } = await supabase.from('fee_payments').delete().eq('id', p.id);
    if (error) toast.error(error.message);
    else {
      logFeeAudit(user?.email, 'delete_payment', selected, { receipt_number: p.receipt_number, amount: p.amount, detail: `${p.fee_head} · ${p.payment_mode} · ${p.payment_date}` });
      if (p.receipt_number) {
        const { error: aErr } = await supabase.from('accounts_transactions').delete().eq('source','fees').eq('reference', p.receipt_number);
        if (aErr) toast.error('Payment deleted, but the Accounts entry could not be removed: ' + aErr.message + ' (remove it manually in Accounts)');
      }
      loadAllPayments();
      toast.success('Payment deleted');
      // Reset and redistribute all payments to schedules
      await redistributeAllPayments(selected.id);
      selectStudent(selected);
    }
  }

  async function addPayment() {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    const today = localDate();
    if (!form.credit_head) { toast.error('Select the payment mode'); return; }
    if (!form.payment_date) { toast.error('Select the payment date'); return; }
    if (form.payment_date > today) { toast.error('Payment date cannot be in the future'); return; }
    // Soft guards (can be overridden): paying more than is due, or the same payment twice
    const amountNow = Number(form.amount);
    const dueNow = editPayment ? balance + Number(editPayment.amount||0) : balance;
    if (amountNow > dueNow && !window.confirm(`This payment (₹${amountNow.toLocaleString('en-IN')}) is more than the balance due (₹${Math.max(0,dueNow).toLocaleString('en-IN')}). Record it anyway?`)) return;
    if (!editPayment && studentPayments.some(x => x.payment_date === form.payment_date && Number(x.amount) === amountNow) &&
        !window.confirm(`A payment of ₹${amountNow.toLocaleString('en-IN')} on ${form.payment_date} is already recorded for this student. Record another one?`)) return;
    setSaving(true);
    if (editPayment) {
      const { error } =
    await supabase.from('fee_payments').update({...form, amount: Number(form.amount) }).eq('id', editPayment.id);
      if (error) toast.error(error.message);
      else {
        toast.success('Payment updated');
        logFeeAudit(user?.email, 'edit_payment', selected, { receipt_number: editPayment.receipt_number, amount: form.amount,
          detail: `was ₹${Number(editPayment.amount)} ${editPayment.fee_head}/${editPayment.payment_mode} ${editPayment.payment_date} → ₹${Number(form.amount)} ${form.fee_head}/${form.payment_mode} ${form.payment_date}` });
        if (editPayment.receipt_number) {
          const { error: aErr } = await supabase.from('accounts_transactions').update({
            date: form.payment_date, category: feeCategory(form.fee_head), payment_mode: accountsMode(form.credit_head, form.payment_mode), payment_type: form.payment_type, amount: Number(form.amount),
          }).eq('source','fees').eq('reference', editPayment.receipt_number);
          if (aErr) toast.error('Payment updated, but the Accounts entry could not be updated: ' + aErr.message);
        }
        loadAllPayments();
        // Reset and redistribute all payments to schedules
        await redistributeAllPayments(selected.id);
        setShowModal(false); setEditPayment(null); selectStudent(selected);
        setForm({ fee_head: 'tuition', amount: '', payment_mode: 'cash', payment_type: 'Cash', payment_date: localDate(), credit_head: '', transaction_ref: '', remarks: '' });
      }
    } else {
      let receipt_number = await nextReceiptNo(form.payment_date);
      let { data: feeData, error } = await supabase.from('fee_payments').insert({ student_id: selected.id, receipt_number, received_by: staff?.id, ...form, amount: Number(form.amount) }).select().single();
      if (error && (error.code === '23505' || /duplicate|unique/i.test(error.message || ''))) {
        // another user took this number a moment ago — take the next one
        receipt_number = await nextReceiptNo(form.payment_date, 1);
        ({ data: feeData, error } = await supabase.from('fee_payments').insert({ student_id: selected.id, receipt_number, received_by: staff?.id, ...form, amount: Number(form.amount) }).select().single());
      }
      if (error) toast.error(error.message);
      else {
        toast.success('Payment recorded · ' + receipt_number);
        logFeeAudit(user?.email, 'add_payment', selected, { receipt_number, amount: form.amount, detail: `${form.fee_head} · ${form.payment_mode}${form.credit_head ? ' → ' + form.credit_head : ''} · ${form.payment_date}` });
        // ── Auto-distribute to fee schedule (full redistribute: correct for back-dated payments too) ──
        if (feeData?.id && studentSchedule.length) {
          await redistributeAllPayments(selected.id);
          const { data: schedNow } = await supabase.from('student_fee_schedules').select('*').eq('student_id', selected.id).order('installment_number');
          setStudentSchedule(schedNow || []);
        }
        // ── Auto-sync to Accounts (Cash In). The receipt number is the link used by edit / delete. ──
        // FEES-ENTRYBY
        const { error: accErr } = await supabase.from('accounts_transactions').insert({
          company: 'ARMSTECH',
          date: form.payment_date,
          type: 'Cash In',
          category: feeCategory(form.fee_head),
          payment_mode: accountsMode(form.credit_head, form.payment_mode),
          payment_type: form.payment_type, // PAYMENT-TYPE
          amount: Number(form.amount),
          reference: receipt_number,
          description: `Student fee — ${selected.full_name} (${selected.register_number})`,
          source: 'fees',
          entry_by: staff?.full_name || null,
        });
        if (accErr) toast.error('Payment saved, but it was NOT posted to Accounts (' + accErr.message + '). Add it there manually: ' + receipt_number, { duration: 9000 });
        setShowModal(false);
        setForm({ fee_head: 'tuition', amount: '', payment_mode: 'cash', payment_type: 'Cash', payment_date: localDate(), credit_head: '', transaction_ref: '', remarks: '' });
        // ── Reload payments first, then update header and send WhatsApp ──
        const { data: freshPayments } = await supabase.from('fee_payments').select('*').eq('student_id', selected.id).order('payment_date', { ascending: false });
        setStudentPayments(freshPayments || []);
        loadAllPayments();
        const fmtINR    = n=>'₹'+Number(n||0).toLocaleString('en-IN');
        const totalPaid = (freshPayments||[]).reduce((a,p)=>a+Number(p.amount||0),0);
        const totalFees = Number(selected.fee_total||0);
        const balance   = Math.max(0,totalFees-totalPaid);
        const feeLabel  = {'tuition':'Tuition Fees','hostel':'Hostel Fees','attestation':'Attestation Fees','package_change':'Package Change Fees','manpower':'Manpower Fees','other':'Other Fees'};
        const modeLabel = {'cash':'Cash','online':'Online Transfer','cheque':'Cheque','dd':'Demand Draft','upi':'UPI'};
        const msg = [
          '🏢 *Armstech Engineers Pvt Ltd*',
          '─────────────────────────────',
          '📄 *FEE RECEIPT*',
          '',
          `📋 Receipt No: *${receipt_number}*`,
          `📅 Date: *${form.payment_date}*`,
          '',
          '👤 *Student Details*',
          `Name: ${selected.full_name}`,
          `Register No: ${selected.register_number}`,
          '',
          '💳 *Payment Details*',
          `Fees Head: ${feeLabel[form.fee_head]||form.fee_head}`,
          `Payment Type: ${form.payment_type || 'Cash'}`,
          `Payment Mode: ${form.credit_head || modeLabel[form.payment_mode]||form.payment_mode}`,
          form.transaction_ref ? `Reference: ${form.transaction_ref}` : null,
          form.remarks ? `Remarks: ${form.remarks}` : null,
          '',
          '💰 *Fee Summary*',
          `Total Course Fees: *${fmtINR(totalFees)}*`,
          `This Payment: *${fmtINR(form.amount)}*`,
          `Total Paid till Date: *${fmtINR(totalPaid)}*`,
          `Balance Due: *${fmtINR(balance)}*`,
          balance<=0?'✅ *FEES FULLY PAID*':`⚠️ Balance remaining: ${fmtINR(balance)}`,
          '',
          '─────────────────────────────',
          '📞 For queries: mail@armstechtraining.in',
          '_Note: Fees once paid are non-refundable_',
        ].filter(l=>l!=null).join('\n');
        // Use whatsapp_number if available, else split contact_number by '/' and take last part
        const rawPhone = selected.whatsapp_number || selected.contact_number || '';
        const phone = (rawPhone.includes('/')?rawPhone.split('/').pop():rawPhone).replace(/\D/g,'');
        const intlPhone = phone.startsWith('91')?phone:phone.length===10?'91'+phone:phone;
        // Ask about guardian first (before opening WhatsApp, to avoid focus loss)
        // Guardian contact may have two numbers separated by '/' - use last (WhatsApp) number
        const rawGphone = selected.guardian_contact||'';
        const gphone = (rawGphone.includes('/')?rawGphone.split('/').pop():rawGphone).replace(/\D/g,'');
        const gIntl = gphone && gphone!==phone ? (gphone.startsWith('91')?gphone:gphone.length===10?'91'+gphone:gphone) : null;
        const sendToGuardian = gIntl && window.confirm(`Also send to guardian (${selected.guardian_name||'Guardian'}: ${gphone})?`);
        // Now open WhatsApp for student
        if (intlPhone) window.open(`whatsapp://send?phone=${intlPhone}&text=${encodeURIComponent(msg)}`,'_self');
        // Then open for guardian with same message but with greeting prefix
        if (sendToGuardian) {
          const guardianMsg = `Dear Mr/Ms ${selected.guardian_name||'Guardian'},
Fee update for your ward ${selected.full_name}:

${msg}`;
          setTimeout(()=>{ window.open(`whatsapp://send?phone=${gIntl}&text=${encodeURIComponent(guardianMsg)}`,'_self'); }, 2000);
        }
      }
    }
    setSaving(false);
  }

  const [allPayments, setAllPayments] = useState([]);
  const [todayQuick, setTodayQuick] = useState([]);   // today's payments only - loads in a moment, before the full history
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [batchFilter, setBatchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { loadTodayQuick(); loadStudents(); loadAllPayments(); }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadTodayQuick(); loadStudents(); loadAllPayments(); } catch(e){} }, 800); };
    const ch = supabase.channel('fees-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'fee_payments'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'student_fee_schedules'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);


  async function loadTodayQuick() {
    try {
      const { data } = await supabase.from('fee_payments')
        .select('id, student_id, amount, payment_date, fee_head, payment_mode, payment_type, credit_head, receipt_number, transaction_ref, students(full_name, register_number, batch_id, batch_name)')
        .eq('payment_date', localDate()).order('id').limit(2000);
      setTodayQuick(data || []);
    } catch (e) { /* the full load below still fills the boxes */ }
  }

  async function loadAllPayments() {
    // ALL payments (the old query stopped at 1000, which made the batch summary and date-range totals wrong)
    try {
      const data = await fetchAllPaged(() => supabase
        .from('fee_payments')
        .select('id, student_id, amount, payment_date, fee_head, payment_mode, payment_type, credit_head, receipt_number, transaction_ref, students(full_name, register_number, batch_id, batch_name)')
        .order('payment_date', { ascending: false }).order('id'));
      setAllPayments(data || []);
    } catch (e) { toast.error('Could not load payments: ' + e.message); }
  }

  const today = localDate();

  // Today's stats (memoised so typing in the search box does not recompute them)
  const { todayPayments, todayStudents, todayTotal } = useMemo(() => {
    const tp = allPayments.length ? allPayments.filter(p => p.payment_date === today) : todayQuick.filter(p => p.payment_date === today);
    return { todayPayments: tp, todayStudents: new Set(tp.map(p => p.student_id)).size, todayTotal: tp.reduce((a, p) => a + Number(p.amount || 0), 0) };
  }, [allPayments, todayQuick, today]);

  // Date range stats
  const { rangePayments, rangeTotal, rangeStudents } = useMemo(() => {
    const rp = (dateFrom || dateTo) ? allPayments.filter(p => {
      if (dateFrom && p.payment_date < dateFrom) return false;
      if (dateTo && p.payment_date > dateTo) return false;
      return true;
    }) : [];
    return { rangePayments: rp, rangeTotal: rp.reduce((a, p) => a + Number(p.amount || 0), 0), rangeStudents: new Set(rp.map(p => p.student_id)).size };
  }, [allPayments, dateFrom, dateTo]);

  // Batch summary
  const batchSummary = useMemo(() => {
    const STATUS_ORDER = { ongoing: 0, upcoming: 1, completed: 2 };
    const paidBy = new Map();
    allPayments.forEach(p => paidBy.set(p.student_id, (paidBy.get(p.student_id) || 0) + Number(p.amount || 0)));
    return batches.map(b => {
      const batchStudents = allStudents.filter(st => st.batch_id === b.id || st.batch_name === b.batch_name);
      let batchPaid = 0, batchTotal = 0;
      batchStudents.forEach(st => { batchPaid += paidBy.get(st.id) || 0; batchTotal += Number(st.fee_total || 0); });
      return { ...b, batchTotal, batchPaid, batchBalance: Math.max(0, batchTotal - batchPaid), count: batchStudents.length };
    }).filter(b => b.count > 0)
      .sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3) || a.batch_name.localeCompare(b.batch_name));
  }, [batches, allStudents, allPayments]);

  const filteredBatchSummary = batchFilter
    ? batchSummary.filter(b => b.batch_name === batchFilter)
    : batchSummary;

  const totalFees = Number(selected?.fee_total || 0);
  const totalUtilityBills = utilityBills.reduce((a, b) => a + Number(b.total_amount || 0), 0);
  const totalPayable = totalFees + totalUtilityBills;
  const totalPaid = studentPayments.reduce((a, p) => a + Number(p.amount), 0);
  const balance = totalPayable - totalPaid;

  const filtered = useMemo(() => students.filter(s => {
    const q = searchQ.toLowerCase();
    const matchSearch = !q || s.full_name?.toLowerCase().includes(q) || s.register_number?.toLowerCase().includes(q) || s.contact_number?.includes(q);
    const matchBatch = !filterBatch || (s.batches?.batch_name || s.batch_name) === filterBatch;
    return matchSearch && matchBatch;
  }).sort((a,b) => (a.register_number||'').localeCompare(b.register_number||'')), [students, searchQ, filterBatch]);

  selectStudentRef.current = selectStudent;

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Fee Management</h1><p className="page-subtitle">Track installment payments and outstanding balances</p></div>
        {canEdit('fees') && <button className="btn btn-secondary" style={{whiteSpace:'nowrap'}} onClick={()=>{setShowFeeImport(true);setImportRows([]);setImportBatch('');setImportDone(null);}}>📥 Import Batch Fees</button>}
        <ExportButton
          filename="Fees_Report.xlsx"
          label="Export Fees"
          getData={async () => {
            // All payments
            const payments = await fetchAllPaged(() => supabase
              .from('fee_payments')
              .select('*, students(full_name, register_number, batch_id, batch_name, batches(batch_name)), staff(full_name)')
              .order('payment_date', { ascending: false }).order('id'));

            // All students with fee totals
            const stds = await fetchAllPaged(() => supabase
              .from('students')
              .select('id, full_name, register_number, fee_total, batches(batch_name)')
              .eq('is_active', true).not('student_status','in','("Discontinued","Shifted")').order('full_name').order('id'));

            const paymentData = (payments || []).map((p, i) => ({
              'S.No': i + 1,
              'Receipt No': p.receipt_number || '',
              'Date': fmtDate(p.payment_date),
              'Student Name': p.students?.full_name || '',
              'Register No': p.students?.register_number || '',
              'Batch': p.students?.batches?.batch_name || '',
              'Fee Head': p.fee_head || '',
              'Payment Type': p.payment_type || '',
              'Payment Mode': p.credit_head || p.payment_mode || '',
              'Amount (₹)': Number(p.amount || 0),
              'Transaction Ref': p.transaction_ref || '',
              'Received By': p.staff?.full_name || '',
              'Remarks': p.remarks || '',
            }));

            // Summary per student
            const paidBy = {};
            (payments || []).forEach(p => { paidBy[p.student_id] = (paidBy[p.student_id] || 0) + Number(p.amount || 0); });
            const summaryData = (stds || []).map((s, i) => {
              const paid = paidBy[s.id] || 0;
              return {
                'S.No': i + 1,
                'Student Name': s.full_name || '',
                'Register No': s.register_number || '',
                'Batch': s.batches?.batch_name || '',
                'Total Fees (₹)': Number(s.fee_total || 0),
                'Amount Paid (₹)': paid,
                'Balance Due (₹)': Math.max(0, Number(s.fee_total || 0) - paid),
                'Status': paid >= Number(s.fee_total || 0) ? 'Paid' : paid > 0 ? 'Partial' : 'Pending',
              };
            });

            return [
              { name: 'Payment History', data: paymentData },
              { name: 'Student Fee Summary', data: summaryData },
            ];
          }}
        />
      </div>
      {/* ── Today's Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor:'pointer' }} onClick={()=>setShowTodayModal(true)}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>👥</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Students Paid Today</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--green)' }}>{todayStudents}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
        </div>
        <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor:'pointer' }} onClick={()=>setShowTodayModal(true)}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>💰</div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Collected Today</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)' }}>{fmt(todayTotal)}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{todayPayments.length} transaction{todayPayments.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      </div>

      {/* ── Date Range Filter ── */}
      <div className="card" style={{padding:'14px 18px',marginBottom:16}}>
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:600,color:'var(--text2)'}}>📅 Date Range:</span>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <DateInput value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{fontSize:13,padding:'4px 8px'}}/>
            <span style={{color:'var(--text3)'}}>to</span>
            <DateInput value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{fontSize:13,padding:'4px 8px'}}/>
          </div>
          {(dateFrom||dateTo)&&<button className="btn btn-secondary btn-sm" onClick={()=>{setDateFrom('');setDateTo('');}}>✕ Clear</button>}
        </div>
        {(dateFrom||dateTo)&&(
          <div style={{marginTop:10,display:'flex',gap:20,flexWrap:'wrap',padding:'8px 0',borderTop:'1px solid var(--border)'}}>
            <div style={{fontSize:13}}><span style={{color:'var(--text3)'}}>Total Collected:</span> <strong style={{color:'var(--green)'}}>₹{rangeTotal.toLocaleString('en-IN')}</strong></div>
            <div style={{fontSize:13}}><span style={{color:'var(--text3)'}}>Students Paid:</span> <strong>{rangeStudents}</strong></div>
            <div style={{fontSize:13}}><span style={{color:'var(--text3)'}}>Transactions:</span> <strong>{rangePayments.length}</strong></div>
          </div>
        )}
        {(dateFrom||dateTo)&&rangePayments.length>0&&(
          <div style={{marginTop:8,overflowX:'auto',maxHeight:300,overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead style={{position:'sticky',top:0,background:'var(--accent-light)'}}><tr>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Date</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Student</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Batch</th>
                <th style={{padding:'6px 10px',textAlign:'right'}}>Amount</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Fee Head</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Payment Type</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Payment Mode</th>
                <th style={{padding:'6px 10px',textAlign:'left'}}>Receipt No</th>
              </tr></thead>
              <tbody>{rangePayments.sort((a,b)=>a.payment_date>b.payment_date?-1:1).map((p,i)=>{
                const s = allStudents.find(st=>st.id===p.student_id);
                return <tr key={p.id} style={{borderTop:'1px solid var(--border)',background:i%2?'var(--bg3)':'transparent'}}>
                  <td style={{padding:'5px 10px'}}>{fmtDMY(p.payment_date)}</td>
                  <td style={{padding:'5px 10px',fontWeight:600}}>{s?.full_name||'—'}</td>
                  <td style={{padding:'5px 10px'}}>{s?.batch_name||s?.batches?.batch_name||'—'}</td>
                  <td style={{padding:'5px 10px',textAlign:'right',color:'var(--green)',fontWeight:600}}>₹{Number(p.amount||0).toLocaleString('en-IN')}</td>
                  <td style={{padding:'5px 10px'}}>{p.fee_head||'—'}</td>
                  <td style={{padding:'5px 10px'}}>{p.payment_type||'—'}</td>
                  <td style={{padding:'5px 10px'}}>{p.credit_head||p.payment_mode||'—'}</td>
                  <td style={{padding:'5px 10px'}}>{p.receipt_number||'—'}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: (window.innerWidth<=768)?'1fr':'300px 1fr', gap: 20, minHeight: (window.innerWidth<=768)?'auto':600 }}>
        {/* Student list */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 320px)', position: 'sticky', top: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <SearchableSelect
              options={[
                ...batches.map(b => ({ value: b.batch_name, label: b.batch_name })),
                ...[...new Set(students.map(s => s.batches?.batch_name || s.batch_name).filter(Boolean))]
                  .filter(n => !batches.find(b => b.batch_name === n))
                  .map(n => ({ value: n, label: n }))
              ]}
              value={filterBatch}
              onChange={v => { setFilterBatch(v); setSelected(null); loadStudentsByBatch(v); }}
              placeholder="All Batches"
              searchPlaceholder="Search batch..."
              style={{ width: '100%', marginBottom: 8 }}
            />
            <div className="search-bar" style={{ width: '100%' }}>
              <Search /><input placeholder="Name, mobile, reg no..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {(search || filterBatch) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filtered.length} student{filtered.length !== 1 ? 's' : ''}</span>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setSearch(''); setFilterBatch(''); }}>✕ Clear</button>
              </div>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? <div className="loading" style={{ padding: 30 }}><div className="spinner" /></div> :
              filtered.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                  No students found
                </div>
              ) :
              filtered.map(s => (
                <StudentRow key={s.id} s={s} active={selected?.id === s.id} onPick={pickStudent} />
              ))
            }
          </div>
        </div>

        {/* Fee details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
          {!selected ? (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <div className="empty-state"><IndianRupee /><h3>Select a Student</h3><p>Choose a student to view their fee details</p></div>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                {[
                  { label: 'Course Fees', value: fmt(totalFees), color: 'var(--text)' },
                  { label: 'Utility Bills', value: fmt(totalUtilityBills), color: 'var(--cyan)' },
                  { label: 'Total Payable', value: fmt(totalPayable), color: 'var(--accent)' },
                  { label: 'Amount Paid', value: fmt(totalPaid), color: 'var(--green)' },
                  { label: 'Balance Due', value: fmt(Math.max(0, balance)), color: balance > 0 ? 'var(--red)' : 'var(--green)' },
                ].map(c => (
                  <div className="card" key={c.label} style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.color, fontFamily: 'var(--font-display)' }}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* Fee Structure Breakdown */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 13 }}>Fee Structure</div>
                <div style={{ padding: '4px 0' }}>
                  {[
                    { label: 'Course / Tuition Fees', value: selected.fee_tuition, show: true },
                    { label: 'Hostel Fees', value: selected.hostel_fee, show: Number(selected.hostel_fee) > 0, color: 'var(--cyan)' },
                    { label: 'Attestation Fees', value: selected.fee_attestation, show: Number(selected.fee_attestation) > 0 },
                    { label: 'Package Change Fees', value: selected.fee_package_change, show: Number(selected.fee_package_change) > 0 },
                    { label: 'Manpower Fees', value: selected.fee_manpower, show: Number(selected.fee_manpower) > 0 },
                    { label: 'Certification Fees', value: selected.fee_certification, show: Number(selected.fee_certification) > 0 },
                    { label: 'Other Fees', value: selected.fee_other, show: Number(selected.fee_other) > 0 },
                    { label: 'Discount', value: selected.fee_discount, show: Number(selected.fee_discount) > 0, color: 'var(--green)', prefix: '−' },
                  ].filter(r => r.show).map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 13, color: 'var(--text2)' }}>{r.label}</span>
                      <span style={{ fontWeight: 600, fontSize: 13, color: r.color || 'var(--text)' }}>{r.prefix || ''}{fmt(r.value)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'var(--accent-light)' }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Total Fees</span>
                    <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--accent)' }}>{fmt(selected.fee_total)}</span>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="card" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
                  <span>Payment Progress</span>
                  <span style={{ display: 'flex', gap: 12 }}>
                    {totalUtilityBills > 0 && (
                      <span style={{ color: 'var(--cyan)' }}>Utility Bills: {fmt(totalUtilityBills)}</span>
                    )}
                    <span>{totalPayable > 0 ? ((totalPaid / totalPayable) * 100).toFixed(1) : 0}%</span>
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(100, totalPayable > 0 ? (totalPaid / totalPayable) * 100 : 0)}%`, background: balance > 0 ? 'var(--amber)' : 'var(--green)' }} />
                </div>
              </div>

              {/* Payments table */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Discount Tracking */}
                  {Number(selected?.fee_discount||0)>0&&(
                    <div style={{marginBottom:8}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',
                        background:'var(--green-light)',borderRadius:8,fontSize:12}}>
                        <span style={{fontWeight:600,color:'var(--green)'}}>
                          🎫 Discount Applied: ₹{Number(selected.fee_discount).toLocaleString('en-IN')}
                          {discountLogs.length>0&&<span style={{fontWeight:400,marginLeft:6,color:'var(--text3)'}}>({discountLogs.length} entr{discountLogs.length===1?'y':'ies'})</span>}
                        </span>
                        <div style={{display:'flex',gap:6}}>
                          {discountLogs.length>0&&(
                            <details style={{fontSize:11}}>
                              <summary style={{cursor:'pointer',color:'var(--green)'}}>View log</summary>
                              <div style={{marginTop:6,background:'#fff',borderRadius:6,padding:8}}>
                                {discountLogs.map(d=>(
                                  <div key={d.id} style={{padding:'4px 0',borderBottom:'1px solid var(--border)',fontSize:11}}>
                                    <strong>₹{Number(d.amount).toLocaleString('en-IN')}</strong> — {d.reason}
                                    <span style={{color:'var(--text3)',marginLeft:6}}>by {d.approved_by||d.created_by?.split('@')[0]} on {d.created_at?.split('T')[0]}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                          {canEdit('fees')&&<button className="btn btn-ghost btn-sm" style={{fontSize:10}} onClick={()=>setShowDiscountModal(true)}>+ Add Discount</button>}
                        </div>
                      </div>
                    </div>
                  )}
                  {!Number(selected?.fee_discount||0)&&canEdit('fees')&&(
                    <div style={{marginBottom:8,textAlign:'right'}}>
                      <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--green)'}} onClick={()=>setShowDiscountModal(true)}>🎫 Add Discount</button>
                    </div>
                  )}

                  {/* Refunds & Transfers */}
                  <div style={{marginBottom:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',
                      background:'var(--surface2)',borderRadius:8,cursor:'pointer'}}
                      onClick={()=>setShowAdjust(s=>!s)}>
                      <span style={{fontWeight:600,fontSize:13}}>💸 Refunds & Transfers {adjustments.length>0?`(${adjustments.length})`:''}</span>
                      <div style={{display:'flex',gap:8,alignItems:'center'}}>
                        {(isEditor||staff) && <button className="btn btn-primary btn-sm" onClick={e=>{e.stopPropagation();setShowAdjModal(true);}}>+ New Request</button>}
                        <span>{showAdjust?'▲':'▼'}</span>
                      </div>
                    </div>
                    {showAdjust&&(
                      <div style={{border:'1px solid var(--border)',borderTop:'none',borderRadius:'0 0 8px 8px',padding:12}}>
                        {adjustments.length===0?(
                          <div style={{textAlign:'center',padding:12,color:'var(--text3)',fontSize:12}}>No refund or transfer requests</div>
                        ):(
                          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                            <thead>
                              <tr style={{background:'var(--surface2)'}}>
                                {['Type','Amount','Reason','Status','Requested By','Approved By',''].map(h=>(
                                  <th key={h} style={{padding:'5px 10px',textAlign:'left'}}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {adjustments.map(adj=>(
                                <tr key={adj.id} style={{borderBottom:'1px solid var(--border)'}}>
                                  <td style={{padding:'6px 10px'}}>
                                    <span style={{padding:'2px 8px',borderRadius:8,fontSize:11,fontWeight:600,
                                      background:adj.type==='refund'?'var(--red-light)':'var(--blue-light)',
                                      color:adj.type==='refund'?'var(--red)':'var(--blue)'}}>
                                      {adj.type==='refund'?'💰 Refund':'🔄 Transfer'}
                                    </span>
                                  </td>
                                  <td style={{padding:'6px 10px',fontWeight:600}}>₹{Number(adj.amount).toLocaleString('en-IN')}</td>
                                  <td style={{padding:'6px 10px',color:'var(--text3)'}}>{adj.reason||'—'}</td>
                                  <td style={{padding:'6px 10px'}}>
                                    <span style={{padding:'2px 6px',borderRadius:8,fontSize:11,
                                      background:adj.status==='approved'?'var(--green-light)':adj.status==='rejected'?'var(--red-light)':'var(--amber-light)',
                                      color:adj.status==='approved'?'var(--green)':adj.status==='rejected'?'var(--red)':'var(--amber)'}}>
                                      {adj.status}
                                    </span>
                                  </td>
                                  <td style={{padding:'6px 10px',color:'var(--text3)',fontSize:11}}>{adj.created_by?.split('@')[0]||'—'}</td>
                                  <td style={{padding:'6px 10px',color:'var(--text3)',fontSize:11}}>{adj.approved_by?.split('@')[0]||'—'}</td>
                                  <td style={{padding:'6px 10px'}}>
                                    {adj.status==='pending'&&canApproveAdj&&(
                                      <div style={{display:'flex',gap:4}}>
                                        <button className="btn btn-sm" style={{background:'var(--green-light)',color:'var(--green)',fontSize:11}}
                                          onClick={()=>approveAdjustment(adj)}>✅ Approve</button>
                                        <button className="btn btn-sm" style={{background:'var(--red-light)',color:'var(--red)',fontSize:11}}
                                          onClick={()=>rejectAdjustment(adj)}>❌ Reject</button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>

                  <span style={{ fontWeight: 600, fontSize: 14, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}
                    onClick={()=>setShowSchedule(s=>!s)}>
                    📅 Fee Schedule {studentSchedule.length>0?`(${studentSchedule.filter(s=>s.status==='paid').length}/${studentSchedule.length} paid)`:''}
                    {studentSchedule.filter(s=>s.status!=='paid'&&s.status!=='waived'&&s.due_date&&s.due_date<today).length>0&&(
                      <span style={{marginLeft:6,padding:'1px 7px',borderRadius:8,fontSize:11,fontWeight:700,background:'var(--red)',color:'#fff'}}>
                        🚨 {studentSchedule.filter(s=>s.status!=='paid'&&s.status!=='waived'&&s.due_date&&s.due_date<today).length} OVERDUE
                      </span>
                    )}
                    {studentSchedule.filter(s=>s.status!=='paid'&&s.status!=='waived'&&s.due_date&&s.due_date>=today&&s.due_date<=new Date(Date.now()+7*86400000).toISOString().split('T')[0]).length>0&&(
                      <span style={{marginLeft:4,padding:'1px 7px',borderRadius:8,fontSize:11,fontWeight:700,background:'var(--amber)',color:'#fff'}}>
                        ⚠️ Due Soon
                      </span>
                    )}
                    <span style={{fontSize:11,color:'var(--text3)'}}>{showSchedule?'▲':'▼'}</span>
                  </span>
                </div>
                {showSchedule && studentSchedule.length>0 && (
                  <div style={{padding:'0 0 8px 0'}}>
                    {(()=>{
                      const totalFees = studentSchedule.reduce((a,s)=>a+Number(s.amount),0);
                      const totalPaid = studentSchedule.reduce((a,s)=>a+Number(s.paid_amount||0),0);
                      const today = new Date().toISOString().split('T')[0];
                      return (<>
                        <div style={{display:'flex',gap:16,padding:'8px 12px',background:'var(--surface2)',fontSize:12}}>
                          <span>Total: <strong>₹{totalFees.toLocaleString('en-IN')}</strong></span>
                          <span style={{color:'var(--green)'}}>Paid: <strong>₹{totalPaid.toLocaleString('en-IN')}</strong></span>
                          <span style={{color:'var(--red)'}}>Balance: <strong>₹{(totalFees-totalPaid).toLocaleString('en-IN')}</strong></span>
                        </div>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                          <thead>
                            <tr style={{background:'var(--accent-light)'}}>
                              {['#','Installment','Amount','Paid','Paid Date','Due Date','Status'].map(h=>(
                                <th key={h} style={{padding:'6px 10px',textAlign:h==='Amount'||h==='Paid'?'right':'left'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {studentSchedule.map((sc,i)=>{
                              const isOverdue = sc.status!=='paid'&&sc.status!=='waived'&&sc.due_date&&sc.due_date<today;
                              const isDueSoon = sc.status!=='paid'&&sc.due_date&&sc.due_date>=today&&sc.due_date<=new Date(Date.now()+7*86400000).toISOString().split('T')[0];
                              const bg = sc.status==='paid'?'#f0fdf4':isOverdue?'#fef2f2':isDueSoon?'#fffbeb':'';
                              return (
                                <tr key={sc.id} style={{borderBottom:'1px solid var(--border)',background:bg}}>
                                  <td style={{padding:'6px 10px',color:'var(--text3)'}}>{sc.installment_number}</td>
                                  <td style={{padding:'6px 10px',fontWeight:500}}>{sc.label}</td>
                                  <td style={{padding:'6px 10px',textAlign:'right'}}>₹{Number(sc.amount).toLocaleString('en-IN')}</td>
                                  <td style={{padding:'6px 10px',textAlign:'right',color:'var(--green)',fontWeight:500}}>
                                    {sc.paid_amount>0?'₹'+Number(sc.paid_amount).toLocaleString('en-IN'):'—'}
                                  </td>
                                  <td style={{padding:'6px 10px',fontSize:11,color:'var(--text3)'}}>
                                    {sc.payment_dates?.length>0?(
                                      <div>{sc.payment_dates.map((d,i)=>(
                                        <div key={i}>{new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                                      ))}</div>
                                    ):sc.paid_date?new Date(sc.paid_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—'}
                                  </td>
                                  <td style={{padding:'6px 10px'}}>
                                    {sc.due_date?new Date(sc.due_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})
                                      :<span style={{color:'var(--amber)',fontSize:11}}>Not set</span>}
                                  </td>
                                  <td style={{padding:'6px 10px'}}>
                                    {sc.status==='paid'?<span className="badge badge-green">✓ Paid</span>
                                      :sc.status==='partial'?<span className="badge badge-yellow">Partial</span>
                                      :isOverdue?<span className="badge badge-red">Overdue</span>
                                      :isDueSoon?<span className="badge badge-yellow">Due Soon</span>
                                      :<span className="badge badge-gray">Pending</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </>);
                    })()}
                  </div>
                )}
                {showSchedule && studentSchedule.length===0 && (
                  <div style={{padding:'8px 12px',fontSize:12,color:'var(--text3)'}}>No fee schedule generated yet.</div>
                )}
                <div style={{borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Payment History ({studentPayments.length})</span>
                  <div style={{display:"flex",gap:8}}><ImportButton configKey="fee_payments" onImported={()=>{ selectStudent(selected); loadAllPayments(); }}/>{canEdit('fees') && <button className="btn btn-primary btn-sm" onClick={() => { setEditPayment(null); setShowModal(true); }}><Plus size={13} /> Add Payment</button>}
                  {balance>0&&(
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#25d366'}}
                      onClick={()=>{
                        const overdueCount = studentSchedule.filter(s=>s.status!=='paid'&&s.status!=='waived'&&s.due_date&&s.due_date<today).length;
                        const msg = `*Fee Reminder — Armstech Engineers*\n`+
                          `Student: ${selected.full_name}\n`+
                          `Reg No: ${selected.register_number||''}\n`+
                          `Total Fee: ₹${Number(selected.fee_total||0).toLocaleString('en-IN')}\n`+
                          `Paid: ₹${Number(totalPaid||0).toLocaleString('en-IN')}\n`+
                          `Balance Due: ₹${Number(balance||0).toLocaleString('en-IN')}\n`+
                          (overdueCount>0?`⚠️ ${overdueCount} overdue installment(s). Please pay immediately.\n`:'')+
                          `\nPlease contact us to clear your dues.\n_Armstech Engineers Pvt Ltd_`;
                        navigator.clipboard.writeText(msg).then(()=>toast.success('Message copied! Paste in WhatsApp'));
                      }}>
                      💬 Fee Reminder
                    </button>
                  )}
                </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead><tr><th>Receipt No.</th><th>Date</th><th>Fee Head</th><th>Payment Type</th><th>Payment Mode</th><th>Amount</th><th>Ref.</th><th>Action</th></tr></thead>
                    <tbody>
                      {studentPayments.length === 0 ? (
                        <tr><td colSpan={8}><div className="empty-state" style={{ padding: 30 }}><p>No payments recorded yet</p></div></td></tr>
                      ) : studentPayments.map(p => (
                        <tr key={p.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.receipt_number}</td>
                          <td style={{ fontSize: 12 }}>{fmtDMY(p.payment_date)}</td>
                          <td><span className="badge badge-blue">{p.fee_head?.replace('_', ' ')}</span></td>
                          <td style={{ fontSize: 12 }}>{p.payment_type || (String(p.payment_mode||'').toLowerCase()==='cash' ? 'Cash' : '—')}</td>
                          <td style={{ fontSize: 12 }}>{p.credit_head || p.payment_mode || '—'}</td>
                          <td style={{ fontWeight: 600, color: 'var(--green)' }}>{fmt(p.amount)}</td>
                          <td style={{ fontSize: 11, color: 'var(--text3)' }}>{p.transaction_ref || '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewPayment(p)}><Eye size={13} /></button>
                              <button className="btn btn-ghost btn-sm" title="Print receipt" onClick={() => printReceipt(p, selected, { totalFees: Number(selected.fee_total||0), totalPaid, balance: Math.max(0, Number(selected.fee_total||0) - totalPaid) })}>🖨</button>
                              {canEdit('fees') && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => { setEditPayment(p); setForm({ fee_head: p.fee_head, amount: p.amount, payment_mode: p.payment_mode, payment_type: p.payment_type || (String(p.payment_mode||'').toLowerCase()==='cash' ? 'Cash' : 'Online'), payment_date: p.payment_date, credit_head: p.credit_head || (String(p.payment_mode||'').toLowerCase()==='cash' ? 'Cash' : ''), transaction_ref: p.transaction_ref || '', remarks: p.remarks || '' }); setShowModal(true); }}><Edit2 size={13} /></button>}
                              {canEdit('fees') && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => deletePayment(p)} title="Delete"><Trash2 size={13} /></button>}
                              <button className="btn btn-ghost btn-sm" title="Send WhatsApp to Student" style={{color:'#25d366'}} onClick={()=>{
                                const totalPaid = studentPayments.reduce((a,x)=>a+Number(x.amount||0),0);
                                const totalFees = Number(selected.fee_total||0);
                                const balance = Math.max(0,totalFees-totalPaid);
                                const fmtINR = n=>'₹'+Number(n||0).toLocaleString('en-IN');
                                const feeLabel = {'tuition':'Tuition Fees','hostel':'Hostel Fees','attestation':'Attestation Fees','package_change':'Package Change Fees','manpower':'Manpower Fees','other':'Other Fees'};
                                const modeLabel = {'cash':'Cash','online':'Online Transfer','cheque':'Cheque','dd':'Demand Draft','upi':'UPI'};
                                const msg = ['🏢 *Armstech Engineers Pvt Ltd*','─────────────────────────────','📄 *FEE RECEIPT*','',
                                  `Receipt No: *${p.receipt_number}*`,`Date: *${p.payment_date}*`,'',
                                  'Student: '+selected.full_name,'Register No: '+selected.register_number,'',
                                  'Fees Head: '+(feeLabel[p.fee_head]||p.fee_head),'Payment Mode: '+(modeLabel[p.payment_mode]||p.payment_mode),'',
                                  'Total Course Fees: *'+fmtINR(totalFees)+'*','This Payment: *'+fmtINR(p.amount)+'*',
                                  'Total Paid: *'+fmtINR(totalPaid)+'*','Balance Due: *'+fmtINR(balance)+'*',
                                  balance<=0?'FEES FULLY PAID':'Balance remaining: '+fmtINR(balance),'',
                                  'For queries: mail@armstechtraining.in','_Note: Fees once paid are non-refundable_',
                                ].filter(Boolean).join('\n');
                                const rawP = selected.whatsapp_number||selected.contact_number||'';
                                const phone=(rawP.includes('/')?rawP.split('/').pop():rawP).replace(/\D/g,'');
                                const intlPhone=phone.startsWith('91')?phone:phone.length===10?'91'+phone:phone;
                                if(intlPhone) window.open('whatsapp://send?phone='+intlPhone+'&text='+encodeURIComponent(msg),'_self');
                                else toast.error('No phone number for this student');
                              }}>💬 WA</button>
                              {selected.guardian_contact&&<button className="btn btn-ghost btn-sm" title="Send WhatsApp to Guardian" style={{color:'#128c7e'}} onClick={()=>{
                                const gPaid = studentPayments.reduce((a,x)=>a+Number(x.amount||0),0);
                                const gFees = Number(selected.fee_total||0);
                                const gBal = Math.max(0,gFees-gPaid);
                                const studentMsg2 = [
                                  '🏢 *Armstech Engineers Pvt Ltd*',
                                  '─────────────────────────────',
                                  '📄 *FEE RECEIPT*','',
                                  `Receipt No: *${p.receipt_number}*`,
                                  `Date: *${p.payment_date}*`,'',
                                  'Student: '+selected.full_name,
                                  'Register No: '+selected.register_number,'',
                                  'Total Course Fees: *₹'+gFees.toLocaleString('en-IN')+'*',
                                  'Total Paid: *₹'+gPaid.toLocaleString('en-IN')+'*',
                                  'Balance Due: *₹'+gBal.toLocaleString('en-IN')+'*',
                                  gBal<=0?'✅ *FEES FULLY PAID*':'⚠️ Balance remaining: ₹'+gBal.toLocaleString('en-IN'),'',
                                  '📞 For queries: mail@armstechtraining.in',
                                  '_Note: Fees once paid are non-refundable_',
                                ].join('\n');
                                const gMsg = 'Dear Mr/Ms '+(selected.guardian_name||'Guardian')+',\nFee update for your ward '+selected.full_name+':\n\n'+studentMsg2;
                                const rawGp = selected.guardian_contact||'';
                                const gp=(rawGp.includes('/')?rawGp.split('/').pop():rawGp).replace(/\D/g,'');
                                const gi=gp.startsWith('91')?gp:gp.length===10?'91'+gp:gp;
                                if(gi) window.open('whatsapp://send?phone='+gi+'&text='+encodeURIComponent(gMsg),'_self');
                              }}>👪 Guardian WA</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Utility bills breakdown — always show section */}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--cyan)' }}>🏠 Hostel Utility Bills</span>
                  <span style={{ fontWeight: 700, color: 'var(--cyan)' }}>{utilityBills.length > 0 ? `Total: ${fmt(totalUtilityBills)}` : 'No bills recorded'}</span>
                </div>
                {utilityBills.length === 0 ? (
                  <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No utility bills for this student</div>
                ) : (
                <table>
                  <thead><tr><th>Month/Year</th><th>Electricity</th><th>Water</th><th>Other</th><th>Total</th><th>Status</th></tr></thead>
                  <tbody>
                    {utilityBills.map(b => {
                      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      return (
                        <tr key={b.id}>
                          <td style={{ fontSize: 12, fontWeight: 500 }}>{MONTHS[b.billing_month - 1]} {b.billing_year}</td>
                          <td style={{ fontSize: 12 }}>₹{Number(b.electricity_amount || 0).toLocaleString('en-IN')}</td>
                          <td style={{ fontSize: 12 }}>₹{Number(b.water_amount || 0).toLocaleString('en-IN')}</td>
                          <td style={{ fontSize: 12 }}>₹{Number(b.other_amount || 0).toLocaleString('en-IN')}</td>
                          <td style={{ fontWeight: 700, color: 'var(--cyan)' }}>₹{Number(b.total_amount || 0).toLocaleString('en-IN')}</td>
                          <td><span className={`badge ${b.payment_status === 'paid' ? 'badge-green' : 'badge-red'}`}>{b.payment_status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {canEdit('fees') && <FeeReports batches={batches} isAdmin={!!(isAdmin || isEditor)} />}

      {/* ── Batch-wise Summary — Admin & HR only ── */}
      {canEdit('fees') && <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 24 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>📊 Batch-wise Fee Summary</div>
          <div style={{ minWidth: 220 }}>
            <SearchableSelect
              options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
              value={batchFilter}
              onChange={v => setBatchFilter(v)}
              placeholder="All Batches"
              searchPlaceholder="Search batch..."
            />
          </div>
        </div>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 100, background: '#e6f7f5' }}>
              <tr style={{ background: '#e6f7f5', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'left', background: '#e6f7f5' }}>Batch</th>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'center' }}>Students</th>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'right' }}>Total Fees</th>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'right' }}>Collected</th>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'right' }}>Balance</th>
                <th style={{ padding: '8px 14px', background: '#e6f7f5', textAlign: 'center' }}>Collection %</th>
              </tr>
            </thead>
            <tbody>
              {filteredBatchSummary.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No data found</td></tr>
              ) : filteredBatchSummary.map((b, i) => {
                const pct = b.batchTotal > 0 ? ((b.batchPaid / b.batchTotal) * 100).toFixed(1) : '0';
                const pctColor = Number(pct) >= 75 ? 'var(--green)' : Number(pct) >= 50 ? 'var(--amber)' : 'var(--red)';
                return (
                  <tr key={b.id} style={{ borderTop: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: 13 }}>{b.batch_name}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 13 }}>{b.count}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 500 }}>{fmt(b.batchTotal)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>{fmt(b.batchPaid)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: b.batchBalance > 0 ? 'var(--red)' : 'var(--green)' }}>{fmt(b.batchBalance)}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                        <div style={{ width: 60, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(100, Number(pct))}%`, background: pctColor, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: pctColor }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredBatchSummary.length > 1 && (
                <tr style={{ borderTop: '2px solid var(--accent)', background: 'var(--accent-light)', fontWeight: 700 }}>
                  <td style={{ padding: '10px 14px', fontSize: 13 }}>Total</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 13 }}>{filteredBatchSummary.reduce((a, b) => a + b.count, 0)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, color: 'var(--accent)' }}>{fmt(filteredBatchSummary.reduce((a, b) => a + b.batchTotal, 0))}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, color: 'var(--green)' }}>{fmt(filteredBatchSummary.reduce((a, b) => a + b.batchPaid, 0))}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, color: 'var(--red)' }}>{fmt(filteredBatchSummary.reduce((a, b) => a + b.batchBalance, 0))}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editPayment ? 'Edit Payment' : 'Record Payment'} — {selected?.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowModal(false); setEditPayment(null); }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Fee Head</label>
                  <select value={form.fee_head} onChange={e => setForm(f => ({ ...f, fee_head: e.target.value }))}>
                    {FEE_HEADS.map(h => <option key={h} value={h}>{h.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Amount (₹) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="form-group"><label>Payment Type *</label>
                  <select value={form.payment_type || 'Cash'} onChange={e => setForm(f => ({ ...f, payment_type: e.target.value }))}>
                    <option value="Cash">Cash</option>
                    <option value="Online">Online</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
                <div className="form-group"><label>Payment Mode *</label>
                  <select value={form.credit_head} onChange={e => setForm(f => ({ ...f, credit_head: e.target.value, payment_mode: legacyPayMode(e.target.value), payment_type: defaultPayType(e.target.value) }))}>
                    <option value="">Select Payment Mode</option>
                    {(form.credit_head && !acctModes.includes(form.credit_head)) && <option value={form.credit_head}>{form.credit_head}</option>}
                    {acctModes.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Payment Date</label>
                  <DateInput value={form.payment_date} max={localDate()} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-group"><label>Transaction Ref. (if any)</label>
                  <input value={form.transaction_ref} onChange={e => setForm(f => ({ ...f, transaction_ref: e.target.value }))} />
                </div>
                <div className="form-group"><label>Remarks</label>
                  <input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
                </div>
              </div>
              {form.amount && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--surface)', borderRadius: 8, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: 'var(--text3)' }}>Total Payable (Fees + Utility Bills):</span>
                    <span style={{ fontWeight: 600 }}>{fmt(totalPayable)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text3)' }}>Balance after this payment:</span>
                    <span style={{ fontWeight: 700, color: (balance + (editPayment ? Number(editPayment.amount||0) : 0) - Number(form.amount)) > 0 ? 'var(--amber)' : 'var(--green)' }}>
                      {fmt(Math.max(0, balance + (editPayment ? Number(editPayment.amount||0) : 0) - Number(form.amount)))}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addPayment} disabled={saving}>{saving ? 'Saving...' : editPayment ? 'Update Payment' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW PAYMENT MODAL */}
      {viewPayment && (
        <div className="modal-overlay" onClick={() => setViewPayment(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Payment Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewPayment(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {[
                ['Receipt No.', viewPayment.receipt_number],
                ['Date', viewPayment.payment_date],
                ['Fee Head', viewPayment.fee_head],
                ['Payment Type', viewPayment.payment_type || '—'],
                ['Payment Mode', viewPayment.credit_head || viewPayment.payment_mode || '—'],
                ['Amount', fmt(viewPayment.amount)],
                ['Transaction Ref.', viewPayment.transaction_ref || '—'],
                ['Remarks', viewPayment.remarks || '—'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text3)' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => printReceipt(viewPayment, selected, { totalFees: Number(selected?.fee_total||0), totalPaid, balance: Math.max(0, Number(selected?.fee_total||0) - totalPaid) })}>🖨 Print Receipt</button>
              <button className="btn btn-primary" onClick={() => setViewPayment(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showTodayModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowTodayModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3>💰 Today's Collections — {new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</h3>
              <button className="modal-close" onClick={()=>setShowTodayModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight:'70vh',overflowY:'auto'}}>
              <table>
                <thead><tr>
                  <th>#</th><th>Student</th><th>Register No</th><th>Fee Head</th><th>Amount</th><th>Payment Type</th><th>Payment Mode</th><th>Ref</th>
                </tr></thead>
                <tbody>
                  {todayPayments.length===0
                    ? <tr><td colSpan={8} style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No payments today</td></tr>
                    : todayPayments.map((p,i)=>(
                      <tr key={p.id}>
                        <td style={{fontSize:12,color:'var(--text3)'}}>{i+1}</td>
                        <td style={{fontWeight:600}}>{p.students?.full_name||'—'}</td>
                        <td style={{fontFamily:'monospace',fontSize:12}}>{p.students?.register_number||'—'}</td>
                        <td style={{fontSize:12}}>{p.fee_head||'—'}</td>
                        <td style={{fontWeight:700,color:'var(--accent)'}}>₹{Number(p.amount||0).toLocaleString('en-IN')}</td>
                        <td style={{fontSize:12}}>{p.payment_type||'—'}</td>
                        <td style={{fontSize:12}}>{p.credit_head||p.payment_mode||'—'}</td>
                        <td style={{fontSize:11,color:'var(--text3)'}}>{p.transaction_ref||'—'}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
              <div style={{marginTop:12,padding:'10px 16px',background:'var(--accent-light)',borderRadius:8,fontWeight:700,color:'var(--accent)'}}>
                Total: ₹{Number(todayTotal||0).toLocaleString('en-IN')} across {todayPayments.length} transaction{todayPayments.length!==1?'s':''}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={()=>setShowTodayModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowDiscountModal(false)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <span className="modal-title">🎫 Add Fee Discount — {selected?.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDiscountModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gap:12}}>
                <div className="form-group">
                  <label>Discount Amount (₹) *</label>
                  <input type="number" value={discountForm.amount} onChange={e=>setDiscountForm(f=>({...f,amount:e.target.value}))} placeholder="Enter amount"/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Current discount: ₹{Number(selected?.fee_discount||0).toLocaleString('en-IN')}</div>
                </div>
                <div className="form-group">
                  <label>Reason *</label>
                  <input value={discountForm.reason} onChange={e=>setDiscountForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Scholarship, Staff ward, Financial hardship"/>
                </div>
                <div className="form-group">
                  <label>Approved By</label>
                  <input value={discountForm.approved_by} onChange={e=>setDiscountForm(f=>({...f,approved_by:e.target.value}))} placeholder="Name of approving authority"/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowDiscountModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDiscount}>Apply Discount</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund/Transfer Modal */}
      {showAdjModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowAdjModal(false)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <span className="modal-title">💸 New Refund / Transfer Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowAdjModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Type</label>
                  <div style={{display:'flex',gap:8}}>
                    {['refund','transfer'].map(t=>(
                      <button key={t} className={`btn btn-sm ${adjForm.type===t?'btn-primary':'btn-ghost'}`}
                        onClick={()=>sfa('type',t)}>
                        {t==='refund'?'💰 Refund':'🔄 Transfer to Another Student'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Amount (₹) *</label>
                  <input type="number" value={adjForm.amount} onChange={e=>sfa('amount',e.target.value)}
                    placeholder={`Max: ₹${totalPaid?.toLocaleString('en-IN')||0}`}/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Total paid: ₹{totalPaid?.toLocaleString('en-IN')||0}</div>
                </div>
                <div className="form-group">
                  <label>Reason *</label>
                  <input value={adjForm.reason} onChange={e=>sfa('reason',e.target.value)} placeholder="Reason for refund/transfer"/>
                </div>
                {adjForm.type==='refund'&&<>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <select value={adjForm.payment_mode} onChange={e=>sfa('payment_mode',e.target.value)}>
                      {['Cash','Bank Transfer','Cheque','UPI'].map(m=><option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Reference No</label>
                    <input value={adjForm.reference_no} onChange={e=>sfa('reference_no',e.target.value)} placeholder="Cheque/Transaction ID"/>
                  </div>
                </>}
                {adjForm.type==='transfer'&&(
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>Transfer To (Student) *</label>
                    <select value={adjForm.to_student_id} onChange={e=>sfa('to_student_id',e.target.value)}>
                      <option value="">Select student...</option>
                      {students.filter(s=>s.id!==selected?.id).map(s=>(
                        <option key={s.id} value={s.id}>{s.full_name} ({s.batches?.batch_name||s.batch_name||''})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group" style={{gridColumn:'1/-1'}}>
                  <label>Notes</label>
                  <textarea rows={2} value={adjForm.notes} onChange={e=>sfa('notes',e.target.value)} placeholder="Additional notes..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowAdjModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveAdjustment}>Submit Request</button>
            </div>
          </div>
        </div>
      )}

      {showFeeImport && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowFeeImport(false)}>
          <div className="modal" style={{maxWidth:800}}>
            <div className="modal-header">
              <span className="modal-title">📥 Import Batch Fees from Excel</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowFeeImport(false)}>✕</button>
            </div>
            <div className="modal-body">
              {!importDone ? (
                <>
                  <div style={{marginBottom:12,fontSize:13,color:'var(--text2)'}}>
                    Upload the Armstech batch Excel file. Required columns: <strong>Register No</strong>, <strong>Actual Fees</strong>, <strong>DATE 1–8</strong>, <strong>FEES 1–8</strong>.
                  </div>
                  <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:16}}>
                    <input type="file" accept=".xlsx,.xls" onChange={async e=>{
                      const file = e.target.files[0];
                      if (!file) return;
                      const buf = await file.arrayBuffer();
                      const wb = XLSX.read(buf, {type:'array',cellDates:true});
                      const ws = wb.Sheets[wb.SheetNames[0]];
                      const rows = XLSX.utils.sheet_to_json(ws, {header:1,defval:''});
                      
                      // Find header row (has "Register No")
                      let headerIdx = -1;
                      for (let i=0;i<rows.length;i++){
                        if (rows[i].some(v=>String(v).toLowerCase().includes('register'))){headerIdx=i;break;}
                      }
                      if (headerIdx<0){toast.error('Could not find header row');return;}
                      
                      const headers = rows[headerIdx].map(v=>String(v).trim());
                      const regCol = headers.findIndex(h=>h.toLowerCase().includes('register'));
                      const feesTotalCol = headers.findIndex(h=>h.toLowerCase().includes('actual') && h.toLowerCase().includes('fee'));
                      
                      // Find DATE and FEES columns - handle any column name variations
                      const rawHeaders = rows[headerIdx].map(v=>String(v||'').replace(/\n/g,' ').replace(/\r/g,' ').trim().toUpperCase());
                      const installments = [];
                      for (let i=0;i<rawHeaders.length;i++){
                        const h = rawHeaders[i].replace(/\s+/g,'');
                        // Match DATE, DATE1, DATE 1, FEES DATE etc
                        if (h.match(/^DATE/) && !h.includes('FEES')){
                          const inst = installments.length + 1;
                          installments.push({dateCol:i, amtCol:i+1, num:inst});
                        }
                      }
                      
                      function parseDate(val){
                        if (!val || val==='') return null;
                        // +12h keeps the calendar day correct whatever the timezone shift SheetJS applied
                        if (val instanceof Date) return new Date(val.getTime() + 12*3600*1000).toISOString().split('T')[0];
                        const s = String(val).trim();
                        if (!s || s==='0') return null;
                        // yyyy-mm-dd
                        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0,10);
                        // dd/mm/yy or dd/mm/yyyy or d/m/yyyy
                        const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
                        if (m){
                          const [,d,mo,y] = m;
                          const yr = y.length===2 ? (parseInt(y)<50?'20':'19')+y : y;
                          return `${yr}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`;
                        }
                        return null;
                      }
                      
                      function parseMultiDates(val){
                        if (!val || val==='') return [];
                        if (val instanceof Date) { const d=parseDate(val); return d?[d]:[]; }
                        const s = String(val).trim();
                        // Handle multiple dates separated by - or , or space
                        // e.g. "29/12/2025-29/01/2026" or "29/12/2025 - 29/01/2026"
                        if (s.includes('/') && (s.match(/\/.*-.*\//)||s.match(/,/))){
                          // Split on hyphen or comma between dates
                          const parts = s.split(/\s*[-,]\s*(?=\d)/);
                          const dates = parts.map(p=>parseDate(p.trim())).filter(Boolean);
                          if (dates.length > 1) return dates;
                        }
                        const d = parseDate(s);
                        return d ? [d] : [];
                      }
                      
                      const parsed = [];
                      const batchName = String(rows[0][0]||'').includes('Batch') 
                        ? String(rows[0].find(v=>String(v).match(/^[A-Z]{2}\d+/))||'') 
                        : '';
                      setImportBatch(batchName);
                      
                      for (let r=headerIdx+1;r<rows.length;r++){
                        const row = rows[r];
                        const regNo = String(row[regCol]||'').trim();
                        if (!regNo || !regNo.match(/^[A-Za-z]{2}/)) continue; // FEES-REGEX: accepts EKM/2016/PPI06/.. style registers
                        const actualFees = Number(row[feesTotalCol]||0);
                        
                        for (const inst of installments){
                          const dateVal = row[inst.dateCol];
                          const amount = Number(row[inst.amtCol]||0);
                          if (!amount || amount===0) continue;
                          
                          const dates = parseMultiDates(dateVal);
                          if (dates.length===0) continue;
                          
                          if (dates.length===1){
                            parsed.push({regNo, date:dates[0], amount, inst:inst.num, actualFees});
                          } else {
                            // Split amount equally
                            const splitAmt = Math.floor(amount/dates.length);
                            const remainder = amount - splitAmt*(dates.length-1);
                            dates.forEach((d,i)=>{
                              parsed.push({regNo, date:d, amount:i===dates.length-1?remainder:splitAmt, inst:inst.num+'.'+i, actualFees:i===0?actualFees:0});
                            });
                          }
                        }
                      }
                      
                      setImportRows(parsed);
                      toast.success(`Parsed ${parsed.length} payment records from ${new Set(parsed.map(p=>p.regNo)).size} students`);
                    }}/>
                  </div>
                  
                  {importRows.length>0 && (
                    <>
                      <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
                        Preview: <strong>{importRows.length}</strong> payments from <strong>{new Set(importRows.map(p=>p.regNo)).size}</strong> students
                      </div>
                      <div style={{overflowY:'auto',maxHeight:300,border:'1px solid var(--border)',borderRadius:6}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                          <thead style={{position:'sticky',top:0,background:'#e6f7f5'}}>
                            <tr>
                              <th style={{padding:'6px 10px',background:'#e6f7f5',textAlign:'left'}}>Reg No</th>
                              <th style={{padding:'6px 10px',background:'#e6f7f5',textAlign:'left'}}>Date</th>
                              <th style={{padding:'6px 10px',background:'#e6f7f5',textAlign:'right'}}>Amount</th>
                              <th style={{padding:'6px 10px',background:'#e6f7f5',textAlign:'center'}}>Inst</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importRows.slice(0,50).map((r,i)=>(
                              <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                                <td style={{padding:'5px 10px'}}>{r.regNo}</td>
                                <td style={{padding:'5px 10px'}}>{fmtDMY(r.date)}</td>
                                <td style={{padding:'5px 10px',textAlign:'right'}}>₹{r.amount.toLocaleString('en-IN')}</td>
                                <td style={{padding:'5px 10px',textAlign:'center'}}>{r.inst}</td>
                              </tr>
                            ))}
                            {importRows.length>50 && <tr><td colSpan={4} style={{padding:8,textAlign:'center',color:'var(--text3)'}}>...and {importRows.length-50} more</td></tr>}
                          </tbody>
                        </table>
                      </div>
                      <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'flex-end'}}>
                        <button className="btn btn-secondary" onClick={()=>setImportRows([])}>Clear</button>
                        <button className="btn btn-primary" disabled={importLoading} onClick={async()=>{
                          setImportLoading(true);
                          try {
                            // Get all student IDs for these reg numbers
                            const regNos = [...new Set(importRows.map(r=>r.regNo))];
                            let studs = [];
                            for (let i=0;i<regNos.length;i+=100) {
                              const { data, error } = await supabase.from('students').select('id,register_number,fee_total,fee_tuition').in('register_number', regNos.slice(i,i+100));
                              if (error) throw error;
                              studs = studs.concat(data||[]);
                            }
                            const regMap = {};
                            studs.forEach(s=>{ regMap[s.register_number]=s; });

                            // Re-importing the same sheet must not double the payments: skip rows already in the database
                            const existing = {};
                            for (let i=0;i<studs.length;i+=100) {
                              const ids = studs.slice(i,i+100).map(x=>x.id);
                              const rowsDb = await fetchAllPaged(() => supabase.from('fee_payments').select('student_id,payment_date,amount').in('student_id', ids).order('id'));
                              rowsDb.forEach(x=>{ const k = `${x.student_id}|${x.payment_date}|${Number(x.amount)}`; existing[k]=(existing[k]||0)+1; });
                            }

                            let inserted=0, skipped=0, duplicates=0, failed=0;
                            const toInsert = [];
                            const touched = new Set();
                            const stamp = Date.now();
                            for (const row of importRows){
                              const stud = regMap[row.regNo];
                              if (!stud){skipped++;continue;}
                              const k = `${stud.id}|${row.date}|${Number(row.amount)}`;
                              if (existing[k] > 0) { existing[k]--; duplicates++; continue; }
                              toInsert.push({
                                student_id: stud.id,
                                receipt_number: `IMP-${stamp}-${toInsert.length+1}`,
                                payment_date: row.date,
                                fee_head: 'tuition',
                                amount: row.amount,
                                payment_mode: 'cash',
                                remarks: `Installment ${row.inst} - Batch import`,
                              });
                              touched.add(stud.id);
                            }
                            for (let i=0;i<toInsert.length;i+=100) {
                              const { error } = await supabase.from('fee_payments').insert(toInsert.slice(i,i+100));
                              if (error) { failed += toInsert.slice(i,i+100).length; toast.error('Some rows failed: ' + error.message); }
                              else inserted += toInsert.slice(i,i+100).length;
                            }

                            // Set the fee total for students that have none (keep tuition + total consistent so the Students page recalculates correctly)
                            const feeMap = {};
                            importRows.filter(r=>r.actualFees>0).forEach(r=>{ feeMap[r.regNo]=r.actualFees; });
                            for (const [regNo, fees] of Object.entries(feeMap)){
                              const stud = regMap[regNo];
                              if (stud && fees>0 && (!stud.fee_total || Number(stud.fee_total)===0)){
                                await supabase.from('students').update({fee_total:fees, fee_tuition: Number(stud.fee_tuition)>0 ? stud.fee_tuition : fees}).eq('id',stud.id);
                              }
                            }

                            // Allocate the imported payments to the installment schedules
                            for (const sid of touched) { try { await redistributeAllPayments(sid); } catch(e){} }
                            loadAllPayments();

                            setImportDone({inserted, skipped, duplicates, failed});
                            toast.success(`Imported ${inserted} payments!`);
                          } catch(err){
                            toast.error(err.message);
                          } finally {
                            setImportLoading(false);
                          }
                        }}>
                          {importLoading ? '⏳ Importing...' : `✅ Import ${importRows.length} Records`}
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div style={{textAlign:'center',padding:32}}>
                  <div style={{fontSize:40,marginBottom:12}}>✅</div>
                  <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Import Complete!</div>
                  <div style={{color:'var(--text2)'}}>Inserted: <strong>{importDone.inserted}</strong> payments</div>
                  {importDone.skipped>0 && <div style={{color:'var(--red)'}}>Skipped: <strong>{importDone.skipped}</strong> (register numbers not found)</div>}
                  {importDone.duplicates>0 && <div style={{color:'var(--amber)'}}>Already in the system (not added again): <strong>{importDone.duplicates}</strong></div>}
                  {importDone.failed>0 && <div style={{color:'var(--red)'}}>Failed: <strong>{importDone.failed}</strong></div>}
                  <button className="btn btn-primary" style={{marginTop:16}} onClick={()=>setShowFeeImport(false)}>Done</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
